/**
 * ReportsView.js — GoHost Staff Portal Reports
 *
 * Categories: Financial · Labor · Custom · Exports
 *
 * Data sources:
 *   payPeriods/{id}         — period bounds, status, revenueForPeriod
 *   payroll_ledger          — typed entries: base, tip, commission, overtime_premium, adjustment
 *   employees               — firstName, lastName, position, role
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  collection, query, where, orderBy, limit,
  onSnapshot, getDocs, addDoc, deleteDoc, doc, Timestamp,
} from 'firebase/firestore';
import { db } from '../../firebase';

// ── Formatting ─────────────────────────────────────────────────────────────────
const fmt$ = (n) =>
  `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtHrs = (n) => `${Number(n || 0).toFixed(1)}h`;
const fmtPct = (n) => `${Number(n || 0).toFixed(1)}%`;

function fmtPeriod(id) {
  const parts = (id || '').split('_to_');
  if (parts.length < 2) return id || '—';
  const s = new Date(parts[0] + 'T00:00:00');
  const e = new Date(parts[1] + 'T00:00:00');
  const o = { month: 'short', day: 'numeric' };
  return `${s.toLocaleDateString('en-US', o)} – ${e.toLocaleDateString('en-US', { ...o, year: 'numeric' })}`;
}

// ── Department helper ──────────────────────────────────────────────────────────
function empDept(emp) {
  if (!emp) return 'Other';
  const pos  = emp.position || '';
  const role = emp.role     || '';
  if (role === 'owner' || role === 'manager' || pos === 'Manager' || pos === 'Owner') return 'Management';
  if (['Cook', 'Dishwasher', 'Expo'].includes(pos)) return 'BOH';
  if (['Server', 'Host', 'Bartender'].includes(pos)) return 'FOH';
  return 'Other';
}

const DEPT_COLORS = { FOH: '#3b82f6', BOH: '#f97316', Management: '#8b5cf6', Other: '#94a3b8' };
const ROLE_PALETTE = ['#3b82f6', '#f97316', '#8b5cf6', '#10b981', '#ef4444', '#eab308', '#0ea5e9', '#ec4899'];

const CURRENT_YEAR   = new Date().getFullYear();
const YEAR_OPTIONS   = Array.from({ length: 5 }, (_, i) => CURRENT_YEAR - i);
const FEDERAL_MIN_WAGE = 7.25; // IRS federal minimum wage for FICA Tip Credit (Form 8846)

const SMS_TYPE_LABELS = {
  reservation_confirmation: 'Reservation Confirm',
  reservation_reminder:     'Reservation Reminder',
  reservation_cancelled:    'Reservation Cancelled',
  table_ready:              'Table Ready',
  waitlist_join:            'Waitlist Join',
  waitlist_ready:           'Waitlist Ready',
  gift_card_delivery:       'Gift Card Delivery',
  schedule_published:       'Schedule Published',
  password_reset:           'Password Reset',
};
const fmtSmsType = (t) => SMS_TYPE_LABELS[t] || t || 'Other';

// ── Ledger aggregation ─────────────────────────────────────────────────────────
function aggregateLedger(ledger, empMap) {
  const byId = {};
  ledger.forEach((e) => {
    if (!e.employeeId) return;
    const id = e.employeeId;
    if (!byId[id]) {
      const emp = empMap[id] || {};
      const name = e.employeeName ||
        (emp.firstName ? `${emp.firstName} ${emp.lastName || ''}`.trim() : '') ||
        'Unknown';
      byId[id] = {
        id, name,
        position: emp.position || '',
        role:     emp.role     || 'employee',
        dept:     empDept(emp),
        hours: 0, baseAmount: 0, tipAmount: 0, commAmount: 0,
        bonusAmount: 0, otPremium: 0, otHours: 0,
      };
    }
    const r      = byId[id];
    const amount = Number(e.amount || 0);
    const hours  = Number(e.hours  || 0);
    if      (e.type === 'base')             { r.hours += hours; r.baseAmount += amount; }
    else if (e.type === 'tip')              { r.tipAmount   += amount; }
    else if (e.type === 'commission')       { r.commAmount  += amount; }
    else if (e.type === 'overtime_premium') { r.otHours += hours; r.otPremium += amount; }
    else if (e.type === 'adjustment')       { r.bonusAmount += amount; }
  });
  return Object.values(byId).map((r) => ({
    ...r,
    laborCost:  r.baseAmount + r.otPremium,
    grossTotal: r.baseAmount + r.tipAmount + r.commAmount + r.bonusAmount + r.otPremium,
  })).sort((a, b) => b.grossTotal - a.grossTotal);
}

// ── CSV export ─────────────────────────────────────────────────────────────────
function downloadCSV(rows, filename) {
  const lines = rows.map(r =>
    r.map(v => `"${String(v == null ? '' : v).replace(/"/g, '""')}"`).join(',')
  );
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

// ── Annual ledger data helper (HR reports) ─────────────────────────────────────
// Pay period IDs are formatted "YYYY-MM-DD_to_YYYY-MM-DD" — use prefix to filter by year.
async function fetchAnnualLedgerData(restaurantId, year) {
  const periodsSnap = await getDocs(
    query(
      collection(db, 'restaurants_data', restaurantId, 'payPeriods'),
      orderBy('startDate', 'desc'),
      limit(60),
    )
  );
  const yearPeriods = periodsSnap.docs
    .filter(d => d.id.startsWith(`${year}-`))
    .map(d => ({ id: d.id, ...d.data() }));

  if (!yearPeriods.length) return { periods: [], entries: [] };

  const periodIds = yearPeriods.map(p => p.id);
  const entries = [];
  for (let i = 0; i < periodIds.length; i += 30) {
    const chunk = await getDocs(
      query(
        collection(db, 'restaurants_data', restaurantId, 'payroll_ledger'),
        where('payPeriodId', 'in', periodIds.slice(i, i + 30)),
      )
    );
    chunk.docs.forEach(d => entries.push({ id: d.id, ...d.data() }));
  }
  return { periods: yearPeriods, entries };
}

function aggregateAnnualLedger(entries, empMap) {
  const byId = {};
  entries.forEach(e => {
    if (!e.employeeId) return;
    const id = e.employeeId;
    if (!byId[id]) {
      const emp = empMap[id] || {};
      byId[id] = {
        id,
        name: e.employeeName || (emp.firstName ? `${emp.firstName} ${emp.lastName || ''}`.trim() : 'Unknown'),
        position: emp.position || '',
        role:     emp.role     || 'employee',
        dept:     empDept(emp),
        hours: 0, otHours: 0,
        baseAmount: 0, tipAmount: 0, commAmount: 0, otPremium: 0, bonusAmount: 0,
      };
    }
    const r = byId[id];
    const amt = Number(e.amount || 0);
    const hrs = Number(e.hours  || 0);
    if      (e.type === 'base')             { r.hours      += hrs; r.baseAmount  += amt; }
    else if (e.type === 'tip')              { r.tipAmount   += amt; }
    else if (e.type === 'commission')       { r.commAmount  += amt; }
    else if (e.type === 'overtime_premium') { r.otHours     += hrs; r.otPremium   += amt; }
    else if (e.type === 'adjustment')       { r.bonusAmount += amt; }
  });
  return Object.values(byId).map(r => ({
    ...r,
    totalWages: r.baseAmount + r.otPremium,
    grossTotal: r.baseAmount + r.tipAmount + r.commAmount + r.bonusAmount + r.otPremium,
  })).sort((a, b) => a.name.localeCompare(b.name));
}

// ── Shared sub-components ──────────────────────────────────────────────────────

const StatCard = ({ label, value, sub, accent }) => (
  <div className="bg-white border border-neutral-200 rounded-xl px-5 py-4">
    <p className="text-xs font-semibold text-neutral-400 uppercase tracking-wide mb-1">{label}</p>
    <p className={`text-2xl font-bold leading-tight ${accent || 'text-neutral-800'}`}>{value}</p>
    {sub && <p className="text-xs text-neutral-400 mt-0.5">{sub}</p>}
  </div>
);

const HBar = ({ label, sublabel, value, max, color }) => {
  const pct = max > 0 ? Math.max(2, (value / max) * 100) : 0;
  return (
    <div className="flex items-center gap-3">
      <div className="w-28 shrink-0 text-right">
        <p className="text-sm font-medium text-neutral-700 leading-tight truncate">{label}</p>
        {sublabel && <p className="text-xs text-neutral-400 leading-tight">{sublabel}</p>}
      </div>
      <div className="flex-1 bg-neutral-100 rounded-full h-3 overflow-hidden">
        <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, backgroundColor: color || '#3b82f6' }} />
      </div>
      <div className="w-20 text-right text-sm font-semibold text-neutral-700 tabular-nums">
        {fmt$(value)}
      </div>
    </div>
  );
};

const NoData = ({ text = 'No payroll data for this period.' }) => (
  <div className="py-12 text-center">
    <p className="text-sm text-neutral-400">{text}</p>
  </div>
);

const Spinner = () => (
  <div className="py-12 text-center text-sm text-neutral-400">Loading…</div>
);

const PeriodPicker = ({ periods, selectedId, onChange, loading }) => {
  if (loading) return <div className="h-9 w-52 bg-neutral-100 rounded-lg animate-pulse" />;
  if (!periods.length) return <p className="text-sm text-neutral-400">No pay periods found.</p>;
  return (
    <select
      value={selectedId || ''}
      onChange={e => onChange(e.target.value)}
      className="text-sm border border-neutral-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-neutral-300 bg-white"
    >
      {periods.map(p => <option key={p.id} value={p.id}>{fmtPeriod(p.id)}</option>)}
    </select>
  );
};

const YearPicker = ({ year, onChange }) => (
  <select value={year} onChange={e => onChange(Number(e.target.value))}
    className="text-sm border border-neutral-300 rounded-lg px-3 py-2 focus:outline-none bg-white">
    {YEAR_OPTIONS.map(y => <option key={y} value={y}>{y}</option>)}
  </select>
);

const ExportBtn = ({ onClick, label = 'Export CSV' }) => (
  <div className="flex justify-end pt-1">
    <button onClick={onClick} className="px-4 py-2 text-sm border border-neutral-300 rounded-lg text-neutral-600 hover:bg-neutral-50 transition-colors">
      {label}
    </button>
  </div>
);

const RevenueNotice = () => (
  <div className="flex items-center gap-3 px-4 py-3 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-800">
    <span>⚠</span>
    <span>No revenue entered for this period. Add it in the Payroll tab to unlock ratio analysis.</span>
  </div>
);

// ── REPORT COMPONENTS ──────────────────────────────────────────────────────────

// 1. Labor Cost by Department
const LaborDeptReport = ({ aggregated, period, loading }) => {
  const depts = useMemo(() => {
    const map = {};
    aggregated.forEach(e => {
      if (!map[e.dept]) map[e.dept] = { dept: e.dept, laborCost: 0, hours: 0, otHours: 0, employees: 0 };
      map[e.dept].laborCost += e.laborCost;
      map[e.dept].hours     += e.hours;
      map[e.dept].otHours   += e.otHours;
      map[e.dept].employees += 1;
    });
    return Object.values(map).sort((a, b) => b.laborCost - a.laborCost);
  }, [aggregated]);

  const totalLabor = depts.reduce((s, d) => s + d.laborCost, 0);
  const totalHours = depts.reduce((s, d) => s + d.hours, 0);
  const revenue    = Number(period?.revenueForPeriod || 0);
  const laborPct   = revenue > 0 ? (totalLabor / revenue) * 100 : null;

  const handleExport = () => {
    const total = totalLabor || 1;
    downloadCSV([
      ['Department', 'Labor Cost', 'Hours', 'OT Hours', 'Employees', '% of Total'],
      ...depts.map(d => [d.dept, d.laborCost.toFixed(2), d.hours.toFixed(2), d.otHours.toFixed(2), d.employees, fmtPct((d.laborCost / total) * 100)]),
    ], `labor_by_dept_${period?.id || 'report'}.csv`);
  };

  if (loading) return <Spinner />;

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard label="Total Labor Cost" value={fmt$(totalLabor)} />
        <StatCard label="Total Hours" value={fmtHrs(totalHours)} />
        <StatCard
          label="Labor % of Revenue"
          value={laborPct != null ? fmtPct(laborPct) : '—'}
          sub={laborPct != null ? (laborPct > 35 ? '⚠ Above 35% target' : '✓ Within target') : 'Enter revenue in Payroll'}
          accent={laborPct != null ? (laborPct > 35 ? 'text-red-600' : 'text-emerald-600') : 'text-neutral-400'}
        />
        <StatCard label="Employees" value={aggregated.length} />
      </div>

      {!depts.length ? <NoData /> : (
        <>
          <div className="bg-white border border-neutral-200 rounded-xl p-5">
            <h4 className="text-sm font-semibold text-neutral-700 mb-4">Cost by Department</h4>
            <div className="space-y-3">
              {depts.map(d => (
                <HBar key={d.dept} label={d.dept} sublabel={`${fmtHrs(d.hours)} · ${d.employees} emp`}
                  value={d.laborCost} max={totalLabor} color={DEPT_COLORS[d.dept]} />
              ))}
            </div>
          </div>

          <div className="bg-white border border-neutral-200 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-neutral-50 border-b border-neutral-200">
                <tr>
                  {['Department', 'Labor Cost', 'Hours', 'OT Hours', '% of Total'].map(h => (
                    <th key={h} className={`px-4 py-3 font-semibold text-neutral-500 ${h === 'Department' ? 'text-left' : 'text-right'}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {depts.map(d => (
                  <tr key={d.dept} className="border-b border-neutral-100 last:border-0">
                    <td className="px-4 py-3 font-medium text-neutral-700">
                      <span className="inline-block w-2 h-2 rounded-full mr-2 shrink-0" style={{ backgroundColor: DEPT_COLORS[d.dept] || '#94a3b8' }} />
                      {d.dept}
                    </td>
                    <td className="px-4 py-3 text-right font-semibold">{fmt$(d.laborCost)}</td>
                    <td className="px-4 py-3 text-right text-neutral-600">{fmtHrs(d.hours)}</td>
                    <td className="px-4 py-3 text-right">
                      {d.otHours > 0 ? <span className="text-amber-600 font-medium">{fmtHrs(d.otHours)}</span> : <span className="text-neutral-300">—</span>}
                    </td>
                    <td className="px-4 py-3 text-right text-neutral-500">{totalLabor > 0 ? fmtPct((d.laborCost / totalLabor) * 100) : '—'}</td>
                  </tr>
                ))}
                <tr className="bg-neutral-50 font-semibold border-t-2 border-neutral-300">
                  <td className="px-4 py-3 text-neutral-700">Total</td>
                  <td className="px-4 py-3 text-right">{fmt$(totalLabor)}</td>
                  <td className="px-4 py-3 text-right">{fmtHrs(totalHours)}</td>
                  <td className="px-4 py-3 text-right text-amber-600">{fmtHrs(depts.reduce((s, d) => s + d.otHours, 0))}</td>
                  <td className="px-4 py-3 text-right">100%</td>
                </tr>
              </tbody>
            </table>
          </div>
          <ExportBtn onClick={handleExport} />
        </>
      )}
    </div>
  );
};

// 2. Hours & Overtime
const HoursOTReport = ({ aggregated, loading }) => {
  const totalHours = aggregated.reduce((s, e) => s + e.hours, 0);
  const totalOT    = aggregated.reduce((s, e) => s + e.otHours, 0);
  const otPct      = totalHours > 0 ? (totalOT / totalHours) * 100 : 0;

  const handleExport = () => downloadCSV([
    ['Employee', 'Position', 'Department', 'Reg Hours', 'OT Hours', 'Total Hours', 'OT Cost'],
    ...aggregated.map(e => [e.name, e.position, e.dept, Math.max(0, e.hours - e.otHours).toFixed(2), e.otHours.toFixed(2), e.hours.toFixed(2), e.otPremium.toFixed(2)]),
  ], 'hours_overtime.csv');

  if (loading) return <Spinner />;

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <StatCard label="Total Hours" value={fmtHrs(totalHours)} />
        <StatCard
          label="Overtime Hours"
          value={fmtHrs(totalOT)}
          sub={otPct > 10 ? '⚠ OT above 10% of hours' : 'Within normal range'}
          accent={otPct > 10 ? 'text-amber-600' : 'text-neutral-800'}
        />
        <StatCard label="OT Rate" value={fmtPct(otPct)} sub="of total hours" />
      </div>

      {!aggregated.length ? <NoData /> : (
        <>
          <div className="bg-white border border-neutral-200 rounded-xl overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-neutral-50 border-b border-neutral-200">
                <tr>
                  {['Employee', 'Role', 'Reg Hrs', 'OT Hrs', 'Total Hrs', 'OT Cost'].map(h => (
                    <th key={h} className={`px-4 py-3 font-semibold text-neutral-500 ${h === 'Employee' || h === 'Role' ? 'text-left' : 'text-right'}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {aggregated.map(e => (
                  <tr key={e.id} className="border-b border-neutral-100 last:border-0">
                    <td className="px-4 py-3 font-medium text-neutral-700">{e.name}</td>
                    <td className="px-4 py-3 text-neutral-500">{e.position || '—'}</td>
                    <td className="px-4 py-3 text-right">{fmtHrs(Math.max(0, e.hours - e.otHours))}</td>
                    <td className="px-4 py-3 text-right">
                      {e.otHours > 0 ? <span className="font-medium text-amber-600">{fmtHrs(e.otHours)}</span> : <span className="text-neutral-300">—</span>}
                    </td>
                    <td className="px-4 py-3 text-right font-medium">{fmtHrs(e.hours)}</td>
                    <td className="px-4 py-3 text-right">
                      {e.otPremium > 0 ? fmt$(e.otPremium) : <span className="text-neutral-300">—</span>}
                    </td>
                  </tr>
                ))}
                <tr className="bg-neutral-50 font-semibold border-t-2 border-neutral-300">
                  <td className="px-4 py-3 text-neutral-700" colSpan={2}>Total</td>
                  <td className="px-4 py-3 text-right">{fmtHrs(Math.max(0, totalHours - totalOT))}</td>
                  <td className="px-4 py-3 text-right text-amber-600">{fmtHrs(totalOT)}</td>
                  <td className="px-4 py-3 text-right">{fmtHrs(totalHours)}</td>
                  <td className="px-4 py-3 text-right">{fmt$(aggregated.reduce((s, e) => s + e.otPremium, 0))}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <ExportBtn onClick={handleExport} />
        </>
      )}
    </div>
  );
};

// 3. Labor Cost by Role
const LaborRoleReport = ({ aggregated, loading }) => {
  const byRole = useMemo(() => {
    const map = {};
    aggregated.forEach(e => {
      const k = e.position || 'Unknown';
      if (!map[k]) map[k] = { role: k, laborCost: 0, hours: 0, otHours: 0, employees: 0 };
      map[k].laborCost += e.laborCost; map[k].hours += e.hours;
      map[k].otHours   += e.otHours;  map[k].employees += 1;
    });
    return Object.values(map).sort((a, b) => b.laborCost - a.laborCost);
  }, [aggregated]);

  const totalLabor = byRole.reduce((s, r) => s + r.laborCost, 0);

  const handleExport = () => downloadCSV([
    ['Position', 'Employees', 'Hours', 'OT Hours', 'Labor Cost', '% of Total'],
    ...byRole.map(r => [r.role, r.employees, r.hours.toFixed(2), r.otHours.toFixed(2), r.laborCost.toFixed(2), totalLabor > 0 ? fmtPct((r.laborCost / totalLabor) * 100) : '0%']),
  ], 'labor_by_role.csv');

  if (loading) return <Spinner />;
  if (!byRole.length) return <NoData />;

  return (
    <div className="space-y-5">
      <div className="bg-white border border-neutral-200 rounded-xl p-5">
        <h4 className="text-sm font-semibold text-neutral-700 mb-4">Cost by Position</h4>
        <div className="space-y-3">
          {byRole.map((r, i) => (
            <HBar key={r.role} label={r.role} sublabel={`${fmtHrs(r.hours)} · ${r.employees} emp`}
              value={r.laborCost} max={totalLabor} color={ROLE_PALETTE[i % ROLE_PALETTE.length]} />
          ))}
        </div>
      </div>
      <div className="bg-white border border-neutral-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 border-b border-neutral-200">
            <tr>
              {['Position', 'Employees', 'Hours', 'Labor Cost', '% of Total'].map(h => (
                <th key={h} className={`px-4 py-3 font-semibold text-neutral-500 ${h === 'Position' ? 'text-left' : 'text-right'}`}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {byRole.map(r => (
              <tr key={r.role} className="border-b border-neutral-100 last:border-0">
                <td className="px-4 py-3 font-medium text-neutral-700">{r.role}</td>
                <td className="px-4 py-3 text-right text-neutral-600">{r.employees}</td>
                <td className="px-4 py-3 text-right text-neutral-600">{fmtHrs(r.hours)}</td>
                <td className="px-4 py-3 text-right font-semibold">{fmt$(r.laborCost)}</td>
                <td className="px-4 py-3 text-right text-neutral-500">{totalLabor > 0 ? fmtPct((r.laborCost / totalLabor) * 100) : '—'}</td>
              </tr>
            ))}
            <tr className="bg-neutral-50 font-semibold border-t-2 border-neutral-300">
              <td className="px-4 py-3 text-neutral-700" colSpan={3}>Total</td>
              <td className="px-4 py-3 text-right">{fmt$(totalLabor)}</td>
              <td className="px-4 py-3 text-right">100%</td>
            </tr>
          </tbody>
        </table>
      </div>
      <ExportBtn onClick={handleExport} />
    </div>
  );
};

// 4. Labor Cost Trend (multi-period fetch)
const LaborTrendReport = ({ allPeriods, restaurantId }) => {
  const [trendData, setTrendData] = useState([]);
  const [loading, setLoading]     = useState(true);

  const periodKey = allPeriods.slice(0, 12).map(p => p.id).join(',');

  useEffect(() => {
    const periodsToLoad = allPeriods.slice(0, 12);
    if (!periodsToLoad.length) { setLoading(false); return; }
    setLoading(true);

    Promise.all(periodsToLoad.map(async (period) => {
      const snap = await getDocs(query(
        collection(db, 'restaurants_data', restaurantId, 'payroll_ledger'),
        where('payPeriodId', '==', period.id),
      ));
      const laborCost = snap.docs.reduce((s, d) => {
        const e = d.data();
        return (e.type === 'base' || e.type === 'overtime_premium') ? s + Number(e.amount || 0) : s;
      }, 0);
      const revenue = Number(period.revenueForPeriod || 0);
      return { id: period.id, laborCost, revenue, laborPct: revenue > 0 ? (laborCost / revenue) * 100 : null };
    }))
      .then(data => { setTrendData(data.reverse()); setLoading(false); })
      .catch(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodKey, restaurantId]);

  const maxLabor = Math.max(...trendData.map(d => d.laborCost), 1);

  if (loading) return <Spinner />;
  if (!trendData.length) return <NoData text="No period data found." />;

  return (
    <div className="space-y-5">
      <div className="bg-white border border-neutral-200 rounded-xl p-5">
        <h4 className="text-sm font-semibold text-neutral-700 mb-4">Labor Cost — Last {trendData.length} Periods</h4>
        <div className="space-y-3">
          {trendData.map(d => (
            <div key={d.id} className="flex items-center gap-3">
              <div className="w-28 shrink-0 text-right">
                <p className="text-xs text-neutral-500 leading-tight">{fmtPeriod(d.id).split('–')[0].trim()}</p>
              </div>
              <div className="flex-1 bg-neutral-100 rounded-full h-3 overflow-hidden">
                <div className="h-full rounded-full bg-blue-500 transition-all duration-500" style={{ width: `${Math.max(2, (d.laborCost / maxLabor) * 100)}%` }} />
              </div>
              <div className="w-24 text-right">
                <p className="text-sm font-semibold text-neutral-700 tabular-nums">{fmt$(d.laborCost)}</p>
                {d.laborPct != null && (
                  <p className={`text-xs tabular-nums ${d.laborPct > 35 ? 'text-red-500' : 'text-neutral-400'}`}>{fmtPct(d.laborPct)}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white border border-neutral-200 rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 border-b border-neutral-200">
            <tr>
              {['Period', 'Revenue', 'Labor Cost', 'Labor %', 'After Labor'].map(h => (
                <th key={h} className={`px-4 py-3 font-semibold text-neutral-500 ${h === 'Period' ? 'text-left' : 'text-right'}`}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {[...trendData].reverse().map(d => (
              <tr key={d.id} className="border-b border-neutral-100 last:border-0">
                <td className="px-4 py-3 text-neutral-600 text-xs whitespace-nowrap">{fmtPeriod(d.id)}</td>
                <td className="px-4 py-3 text-right">{d.revenue > 0 ? fmt$(d.revenue) : <span className="text-neutral-300">—</span>}</td>
                <td className="px-4 py-3 text-right font-medium tabular-nums">{fmt$(d.laborCost)}</td>
                <td className="px-4 py-3 text-right">
                  {d.laborPct != null
                    ? <span className={d.laborPct > 35 ? 'text-red-600 font-medium tabular-nums' : 'text-neutral-600 tabular-nums'}>{fmtPct(d.laborPct)}</span>
                    : <span className="text-neutral-300">—</span>}
                </td>
                <td className="px-4 py-3 text-right">
                  {d.revenue > 0
                    ? <span className={`tabular-nums ${d.revenue - d.laborCost >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{fmt$(d.revenue - d.laborCost)}</span>
                    : <span className="text-neutral-300">—</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// 5. Revenue vs Labor
const RevVsLaborReport = ({ aggregated, period, loading }) => {
  const totalLabor = aggregated.reduce((s, e) => s + e.laborCost, 0);
  const revenue    = Number(period?.revenueForPeriod || 0);
  const laborPct   = revenue > 0 ? (totalLabor / revenue) * 100 : null;
  const margin     = revenue > 0 ? revenue - totalLabor : null;

  if (loading) return <Spinner />;

  return (
    <div className="space-y-5">
      {revenue === 0 && <RevenueNotice />}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard label="Revenue" value={revenue > 0 ? fmt$(revenue) : '—'} sub="Entered in Payroll" />
        <StatCard label="Total Labor Cost" value={fmt$(totalLabor)} />
        <StatCard
          label="Labor % of Revenue"
          value={laborPct != null ? fmtPct(laborPct) : '—'}
          sub={laborPct != null ? (laborPct > 35 ? '⚠ Above 35% target' : '✓ Within target') : ''}
          accent={laborPct != null ? (laborPct > 35 ? 'text-red-600' : 'text-emerald-600') : 'text-neutral-400'}
        />
        <StatCard
          label="Gross After Labor"
          value={margin != null ? fmt$(margin) : '—'}
          accent={margin != null && margin < 0 ? 'text-red-600' : 'text-neutral-800'}
        />
      </div>

      {revenue > 0 && totalLabor > 0 && (
        <div className="bg-white border border-neutral-200 rounded-xl p-5">
          <h4 className="text-sm font-semibold text-neutral-700 mb-4">Visual Breakdown</h4>
          <div className="space-y-3">
            <HBar label="Revenue"     value={revenue}           max={revenue} color="#10b981" />
            <HBar label="Labor Cost"  value={totalLabor}        max={revenue} color="#ef4444" />
            <HBar label="After Labor" value={Math.max(0, margin)} max={revenue} color="#3b82f6" />
          </div>
          <p className="text-xs text-neutral-400 mt-3">Gross after labor does not include food cost, overhead, or other operating expenses.</p>
        </div>
      )}
    </div>
  );
};

// 6. Period P&L Summary
const PAID_RES_STATUSES = new Set(['paid', 'bussing', 'completed', 'fullyPaid', 'comped']);

const PeriodPLReport = ({ aggregated, period, loading, restaurantId }) => {
  const baseTotal = aggregated.reduce((s, e) => s + e.baseAmount, 0);
  const otTotal   = aggregated.reduce((s, e) => s + e.otPremium, 0);
  const tipsTotal = aggregated.reduce((s, e) => s + e.tipAmount, 0);
  const laborCost = baseTotal + otTotal;
  const manualRevenue = Number(period?.revenueForPeriod || 0);

  // ── Auto-calculate revenue from actual transactions ───────────────────────
  const [actualRevenue, setActualRevenue]     = useState(null); // null = loading
  const [revenueBreakdown, setRevenueBreakdown] = useState(null);

  useEffect(() => {
    if (!period?.id || !restaurantId) { setActualRevenue(0); return; }
    const [startStr, endStr] = period.id.split('_to_');
    if (!startStr || !endStr) { setActualRevenue(0); return; }

    const startDate = new Date(startStr + 'T00:00:00');
    const endDate   = new Date(endStr   + 'T23:59:59');
    const startMs   = startDate.getTime();
    const endMs     = endDate.getTime();

    let cancelled = false;
    (async () => {
      try {
        const [resSnap, pickupSnap, gcSnap] = await Promise.all([
          getDocs(query(
            collection(db, 'restaurants_data', restaurantId, 'reservations'),
            where('date', '>=', Timestamp.fromDate(startDate)),
            where('date', '<=', Timestamp.fromDate(endDate)),
          )),
          getDocs(query(
            collection(db, 'restaurants_data', restaurantId, 'pickup_orders'),
            where('createdAt', '>=', startMs),
            where('createdAt', '<=', endMs),
          )),
          getDocs(query(
            collection(db, 'restaurants_data', restaurantId, 'giftCards'),
            where('createdAt', '>=', Timestamp.fromDate(startDate)),
            where('createdAt', '<=', Timestamp.fromDate(endDate)),
          )),
        ]);

        if (cancelled) return;

        const dineInRevenue = resSnap.docs.reduce((s, d) => {
          const r = d.data();
          return PAID_RES_STATUSES.has(r.status) ? s + Number(r.total || 0) : s;
        }, 0);

        const paidPickup = pickupSnap.docs.filter(d => d.data().paymentStatus === 'paid');
        const hostRevenue   = paidPickup.filter(d => d.data().source !== 'widget').reduce((s, d) => s + (d.data().payment?.totalCaptured ?? d.data().total ?? 0), 0);
        const widgetRevenue = paidPickup.filter(d => d.data().source === 'widget').reduce((s, d) => s + (d.data().payment?.totalCaptured ?? d.data().total ?? 0), 0);
        const pickupRevenue = hostRevenue + widgetRevenue;

        const gcSold = gcSnap.docs.filter(d => !d.data().isFree && !d.data().isTest)
          .reduce((s, d) => s + (d.data().balanceCents || 0) / 100, 0);

        const total = dineInRevenue + pickupRevenue;
        setActualRevenue(total);
        setRevenueBreakdown({ dineInRevenue, hostRevenue, widgetRevenue, pickupRevenue, gcSold });
      } catch (e) {
        console.warn('[PeriodPLReport] actual revenue fetch failed:', e);
        if (!cancelled) setActualRevenue(manualRevenue);
      }
    })();
    return () => { cancelled = true; };
  }, [period?.id, restaurantId]);

  // Use actual revenue if available, fall back to manual
  const revenue  = actualRevenue ?? manualRevenue;
  const laborPct = revenue > 0 ? (laborCost / revenue) * 100 : null;

  const handleExport = () => downloadCSV([
    ['Metric', 'Amount'],
    ['Period', fmtPeriod(period?.id)],
    ['Revenue (Auto-calculated)', revenue.toFixed(2)],
    ...(revenueBreakdown ? [
      ['  Dine-in', revenueBreakdown.dineInRevenue.toFixed(2)],
      ['  Pickup (Staff)', revenueBreakdown.hostRevenue.toFixed(2)],
      ['  Pickup (Online)', revenueBreakdown.widgetRevenue.toFixed(2)],
      ['  Gift Card Sales (Deferred)', revenueBreakdown.gcSold.toFixed(2)],
    ] : []),
    ['Base Labor Cost', baseTotal.toFixed(2)],
    ['Overtime Premium', otTotal.toFixed(2)],
    ['Total Labor Cost', laborCost.toFixed(2)],
    ['Tips Distributed', tipsTotal.toFixed(2)],
    ['Labor % of Revenue', laborPct != null ? fmtPct(laborPct) : '—'],
    ['Gross After Labor', revenue > 0 ? (revenue - laborCost).toFixed(2) : '—'],
  ], `pl_summary_${period?.id || 'report'}.csv`);

  const plRows = [
    { label: 'Revenue',           value: revenue,            indent: false, sign: 'pos' },
    { label: 'Base Pay',          value: baseTotal,          indent: true,  sign: 'neg' },
    { label: 'Overtime Premium',  value: otTotal,            indent: true,  sign: 'neg' },
    { label: 'Tips Distributed',  value: tipsTotal,          indent: true,  sign: 'neg', note: 'employee-distributed, not employer expense' },
    { label: 'Gross After Labor', value: revenue - laborCost, indent: false, divider: true },
  ];

  if (loading) return <Spinner />;

  return (
    <div className="space-y-5">
      {/* Revenue source breakdown */}
      {revenueBreakdown && (
        <div className="bg-white border border-neutral-200 rounded-xl overflow-hidden">
          <div className="px-5 py-3 border-b border-neutral-100 bg-neutral-50">
            <p className="text-sm font-semibold text-neutral-700">Revenue Breakdown — {fmtPeriod(period?.id)}</p>
            <p className="text-xs text-neutral-400 mt-0.5">Auto-calculated from paid transactions</p>
          </div>
          <div className="px-5 py-1">
            {[
              { label: 'Dine-in',          value: revenueBreakdown.dineInRevenue,  show: true },
              { label: 'Pickup — Staff',   value: revenueBreakdown.hostRevenue,    show: revenueBreakdown.hostRevenue > 0 },
              { label: 'Pickup — Online',  value: revenueBreakdown.widgetRevenue,  show: revenueBreakdown.widgetRevenue > 0 },
            ].filter(r => r.show).map((row, i) => (
              <div key={i} className="flex items-center justify-between py-2.5 border-b border-neutral-50 last:border-0">
                <span className="text-sm text-neutral-600">{row.label}</span>
                <span className="text-sm font-semibold text-neutral-800 tabular-nums">{fmt$(row.value)}</span>
              </div>
            ))}
            <div className="flex items-center justify-between py-3 border-t-2 border-neutral-200 mt-1">
              <span className="text-sm font-bold text-neutral-800">Total Operating Revenue</span>
              <span className="text-sm font-bold text-emerald-600 tabular-nums">{fmt$(revenue)}</span>
            </div>
            {revenueBreakdown.gcSold > 0 && (
              <p className="text-xs text-neutral-400 pb-3">
                + {fmt$(revenueBreakdown.gcSold)} in gift card sales (deferred revenue — recognized at redemption)
              </p>
            )}
          </div>
          {manualRevenue > 0 && Math.abs(manualRevenue - revenue) > 0.01 && (
            <div className="px-5 py-2 bg-amber-50 border-t border-amber-100">
              <p className="text-xs text-amber-700">
                ⚠ Payroll tab has manual revenue of {fmt$(manualRevenue)} — auto-calculated figure above is used for this report.
              </p>
            </div>
          )}
        </div>
      )}
      {actualRevenue === null && <p className="text-xs text-neutral-400">Calculating actual revenue…</p>}
      {revenue === 0 && actualRevenue === 0 && <RevenueNotice />}

      {/* P&L table */}
      <div className="bg-white border border-neutral-200 rounded-xl overflow-hidden">
        <div className="px-5 py-4 border-b border-neutral-100 bg-neutral-50">
          <p className="text-sm font-semibold text-neutral-700">P&L Summary — {fmtPeriod(period?.id)}</p>
        </div>
        <div className="px-5 py-1">
          {plRows.map((row, i) => (
            <div key={i}>
              {row.divider && <div className="border-t-2 border-neutral-300 my-1" />}
              <div className={`flex items-center justify-between py-3 ${row.indent ? 'pl-5' : ''}`}>
                <div>
                  <span className={`text-sm ${row.divider ? 'font-bold text-neutral-800' : row.indent ? 'text-neutral-500' : 'font-medium text-neutral-700'}`}>{row.label}</span>
                  {row.note && <span className="text-xs text-neutral-400 ml-2">({row.note})</span>}
                </div>
                <span className={`text-sm font-semibold tabular-nums ${
                  row.divider ? (row.value >= 0 ? 'text-emerald-600' : 'text-red-600') :
                  row.sign === 'neg' ? 'text-red-500' : 'text-emerald-600'
                }`}>
                  {row.value === 0 && !revenue ? '—' : (row.sign === 'neg' ? '–' : '') + fmt$(Math.abs(row.value))}
                </span>
              </div>
            </div>
          ))}
        </div>
        {laborPct != null && (
          <div className="px-5 py-3 bg-neutral-50 border-t border-neutral-100">
            <p className="text-xs text-neutral-500">
              Labor as % of revenue:{' '}
              <span className={`font-semibold ${laborPct > 35 ? 'text-red-600' : 'text-emerald-600'}`}>{fmtPct(laborPct)}</span>
              {laborPct > 35 && ' — exceeds 35% target'}
            </p>
          </div>
        )}
      </div>
      {aggregated.length > 0 && <ExportBtn onClick={handleExport} label="Export P&L CSV" />}
    </div>
  );
};

// 7. Custom Report Builder
const CUSTOM_METRICS = [
  { id: 'labor_cost', label: 'Labor Cost',       fn: e => e.laborCost,  money: true  },
  { id: 'gross_pay',  label: 'Gross Pay',        fn: e => e.grossTotal, money: true  },
  { id: 'hours',      label: 'Hours Worked',     fn: e => e.hours,      money: false },
  { id: 'ot_hours',   label: 'Overtime Hours',   fn: e => e.otHours,    money: false },
  { id: 'ot_cost',    label: 'Overtime Cost',    fn: e => e.otPremium,  money: true  },
  { id: 'tips',       label: 'Tips Distributed', fn: e => e.tipAmount,  money: true  },
];
const CUSTOM_GROUPS = [
  { id: 'dept',     label: 'Department', key: e => e.dept           },
  { id: 'role',     label: 'Role',       key: e => e.position || 'Unknown' },
  { id: 'employee', label: 'Employee',   key: e => e.name           },
];

const SMS_CUSTOM_METRICS = [
  { id: 'sms_count',    label: 'Messages Sent',   fn: r => r.count,    money: false },
  { id: 'sms_segments', label: 'Segments Billed', fn: r => r.segments, money: false },
  { id: 'sms_failed',   label: 'Failed Messages', fn: r => r.failed,   money: false },
  { id: 'sms_mms',      label: 'MMS Count',       fn: r => r.mms,      money: false },
];

const CustomReportBuilder = ({ aggregated, periods, selectedPeriodId, onPeriodChange, loading, smsLogs, smsLoading, restaurantId }) => {
  const [dataSource, setDataSource] = useState('payroll');
  const [metric,     setMetric]     = useState('labor_cost');
  const [smsMetric,  setSmsMetric]  = useState('sms_count');
  const [groupBy,    setGroupBy]    = useState('dept');
  const [smsGroupBy, setSmsGroupBy] = useState('type');
  const [smsDayRange, setSmsDayRange] = useState(90);
  const [savedName,  setSavedName]  = useState('');
  const [saving,     setSaving]     = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const selMetric = CUSTOM_METRICS.find(m => m.id === metric);
  const selGroup  = CUSTOM_GROUPS.find(g => g.id === groupBy);

  // ── Payroll result ──────────────────────────────────────────────────────────
  const payrollResult = useMemo(() => {
    if (dataSource !== 'payroll') return [];
    const map = {};
    aggregated.forEach(e => {
      const k = selGroup.key(e);
      if (!map[k]) map[k] = { key: k, value: 0 };
      map[k].value += selMetric.fn(e);
    });
    return Object.values(map).sort((a, b) => b.value - a.value);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aggregated, metric, groupBy, dataSource]);

  // ── SMS result ──────────────────────────────────────────────────────────────
  const smsCutoff = useMemo(() => {
    const d = new Date(); d.setDate(d.getDate() - smsDayRange); return d;
  }, [smsDayRange]);

  const smsResult = useMemo(() => {
    if (dataSource !== 'sms') return [];
    const inRange = smsLogs.filter(l => {
      if (!l.createdAt) return false;
      const d = l.createdAt.toDate ? l.createdAt.toDate() : new Date(l.createdAt);
      return d >= smsCutoff && l.status !== 'skipped_optout';
    });
    const map = {};
    inRange.forEach(l => {
      let k;
      if (smsGroupBy === 'type')   k = fmtSmsType(l.type);
      else if (smsGroupBy === 'status') k = l.status || 'unknown';
      else if (smsGroupBy === 'month')  k = l.billingPeriod || '—';
      else k = 'All';
      if (!map[k]) map[k] = { key: k, count: 0, segments: 0, failed: 0, mms: 0 };
      if (l.status === 'failed') { map[k].failed += 1; return; }
      map[k].count    += 1;
      map[k].segments += (l.numSegments || 1);
      map[k].mms      += (l.numMedia || 0);
    });
    const selSmsMetric = SMS_CUSTOM_METRICS.find(m => m.id === smsMetric);
    return Object.values(map)
      .map(r => ({ key: r.key, value: selSmsMetric.fn(r) }))
      .sort((a, b) => b.value - a.value);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [smsLogs, smsMetric, smsGroupBy, smsDayRange, dataSource]);

  const result   = dataSource === 'payroll' ? payrollResult : smsResult;
  const maxVal   = result.length ? Math.max(...result.map(r => r.value), 1) : 1;
  const isLoading = dataSource === 'payroll' ? loading.ledger : smsLoading;

  const formatVal = v => {
    if (dataSource === 'sms') return Math.round(v).toLocaleString();
    return selMetric?.money ? fmt$(v) : fmtHrs(v);
  };

  const handleExport = () => {
    const groupLabel = dataSource === 'payroll' ? selGroup.label : (smsGroupBy === 'type' ? 'Type' : smsGroupBy === 'status' ? 'Status' : 'Month');
    const metricLabel = dataSource === 'payroll' ? selMetric.label : SMS_CUSTOM_METRICS.find(m => m.id === smsMetric)?.label;
    downloadCSV(
      [[groupLabel, metricLabel], ...result.map(r => [r.key, r.value.toFixed(2)])],
      `custom_report_${new Date().toISOString().slice(0,10)}.csv`
    );
  };

  const handleSave = async () => {
    if (!savedName.trim() || !restaurantId) return;
    setSaving(true);
    try {
      await addDoc(collection(db, 'restaurants_data', restaurantId, 'saved_reports'), {
        name:       savedName.trim(),
        reportId:   'custom_build',
        config: dataSource === 'payroll'
          ? { dataSource: 'payroll', metric, groupBy }
          : { dataSource: 'sms', smsMetric, smsGroupBy, smsDayRange },
        createdAt:  new Date(),
      });
      setSavedName('');
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error('Failed to save report:', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5">
      {/* Controls */}
      <div className="bg-white border border-neutral-200 rounded-xl p-5 space-y-4">
        {/* Data source toggle */}
        <div>
          <label className="block text-xs font-semibold text-neutral-400 uppercase tracking-wide mb-1.5">Data Source</label>
          <div className="flex gap-2">
            {[{ id: 'payroll', label: 'Payroll' }, { id: 'sms', label: 'SMS' }].map(s => (
              <button key={s.id} onClick={() => setDataSource(s.id)}
                className={`px-4 py-1.5 text-sm font-medium rounded-lg border transition-colors ${dataSource === s.id ? 'bg-neutral-900 text-white border-neutral-900' : 'border-neutral-300 text-neutral-600 hover:bg-neutral-50'}`}>
                {s.label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap gap-4 items-end">
          {dataSource === 'payroll' ? (
            <>
              <div>
                <label className="block text-xs font-semibold text-neutral-400 uppercase tracking-wide mb-1.5">Metric</label>
                <select value={metric} onChange={e => setMetric(e.target.value)}
                  className="text-sm border border-neutral-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-neutral-300 bg-white">
                  {CUSTOM_METRICS.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-neutral-400 uppercase tracking-wide mb-1.5">Group By</label>
                <select value={groupBy} onChange={e => setGroupBy(e.target.value)}
                  className="text-sm border border-neutral-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-neutral-300 bg-white">
                  {CUSTOM_GROUPS.map(g => <option key={g.id} value={g.id}>{g.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-neutral-400 uppercase tracking-wide mb-1.5">Period</label>
                <PeriodPicker periods={periods} selectedId={selectedPeriodId} onChange={onPeriodChange} loading={loading.periods} />
              </div>
            </>
          ) : (
            <>
              <div>
                <label className="block text-xs font-semibold text-neutral-400 uppercase tracking-wide mb-1.5">Metric</label>
                <select value={smsMetric} onChange={e => setSmsMetric(e.target.value)}
                  className="text-sm border border-neutral-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-neutral-300 bg-white">
                  {SMS_CUSTOM_METRICS.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-neutral-400 uppercase tracking-wide mb-1.5">Group By</label>
                <select value={smsGroupBy} onChange={e => setSmsGroupBy(e.target.value)}
                  className="text-sm border border-neutral-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-neutral-300 bg-white">
                  <option value="type">Message Type</option>
                  <option value="status">Status</option>
                  <option value="month">Month</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-neutral-400 uppercase tracking-wide mb-1.5">Date Range</label>
                <div className="flex gap-1">
                  {[30, 90, 365].map(d => (
                    <button key={d} onClick={() => setSmsDayRange(d)}
                      className={`px-3 py-2 text-sm font-medium rounded-lg border transition-colors ${smsDayRange === d ? 'bg-neutral-900 text-white border-neutral-900' : 'border-neutral-300 text-neutral-600 hover:bg-neutral-50'}`}>
                      {d === 365 ? '1yr' : `${d}d`}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Result */}
      {isLoading ? <Spinner /> : result.length > 0 ? (
        <>
          <div className="bg-white border border-neutral-200 rounded-xl p-5">
            <h4 className="text-sm font-semibold text-neutral-700 mb-4">
              {dataSource === 'payroll'
                ? `${selMetric.label} — ${selGroup.label}`
                : `${SMS_CUSTOM_METRICS.find(m => m.id === smsMetric)?.label} — by ${smsGroupBy === 'type' ? 'Message Type' : smsGroupBy === 'status' ? 'Status' : 'Month'}`
              }
            </h4>
            <div className="space-y-3">
              {result.map((r, i) => (
                <div key={r.key} className="flex items-center gap-3">
                  <div className="w-32 shrink-0 text-right">
                    <p className="text-sm font-medium text-neutral-700 leading-tight truncate">{r.key}</p>
                  </div>
                  <div className="flex-1 bg-neutral-100 rounded-full h-3 overflow-hidden">
                    <div className="h-full rounded-full transition-all duration-500"
                      style={{ width: `${Math.max(2, (r.value / maxVal) * 100)}%`, backgroundColor: ROLE_PALETTE[i % ROLE_PALETTE.length] }} />
                  </div>
                  <div className="w-20 text-right text-sm font-semibold text-neutral-700 tabular-nums">{formatVal(r.value)}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <ExportBtn onClick={handleExport} />
            {/* Save report */}
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={savedName}
                onChange={e => setSavedName(e.target.value)}
                placeholder="Name this report…"
                className="text-sm border border-neutral-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-neutral-300 w-44"
              />
              <button onClick={handleSave} disabled={!savedName.trim() || saving}
                className="px-4 py-2 text-sm font-semibold border border-neutral-300 rounded-lg text-neutral-700 hover:bg-neutral-50 disabled:opacity-40 transition-colors">
                {saveSuccess ? '✓ Saved' : saving ? 'Saving…' : 'Save Report'}
              </button>
            </div>
          </div>
        </>
      ) : (
        <NoData text="No data for this selection." />
      )}
    </div>
  );
};

// ── HR REPORTS ────────────────────────────────────────────────────────────────

// HR-1: Employee Roster
const HRRosterReport = ({ restaurantId }) => {
  const [emps, setEmps]       = useState(null);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    getDocs(collection(db, 'restaurants_data', restaurantId, 'employees'))
      .then(snap => setEmps(snap.docs.map(d => ({ id: d.id, ...d.data() }))))
      .catch(() => setEmps([]));
  }, [restaurantId]);

  const displayed = emps ? (showAll ? emps : emps.filter(e => e.isActive !== false)) : [];

  const handleExport = () => {
    const rows = [
      ['Name', 'Role', 'Position', 'Department', 'Pay Type', 'Rate / Salary', 'Email', 'Phone', 'Hire Date', 'Status'],
      ...displayed.map(e => {
        const name = e.firstName ? `${e.firstName} ${e.lastName || ''}`.trim() : e.name || '';
        const rate = e.payType === 'salary'
          ? `$${(e.annualSalary || 0).toLocaleString()} / yr`
          : `$${(e.hourlyRate || 0).toFixed(2)} / hr`;
        const hireDate = e.hireDate?.seconds
          ? new Date(e.hireDate.seconds * 1000).toLocaleDateString()
          : e.hireDate || '';
        return [name, e.role || '', e.position || '', e.department || '',
                e.payType || '', rate, e.email || '', e.phone || '', hireDate,
                e.isActive === false ? 'Terminated' : 'Active'];
      }),
    ];
    downloadCSV(rows, `employee_roster_${new Date().toISOString().slice(0, 10)}.csv`);
  };

  if (!emps) return <Spinner />;
  const active     = emps.filter(e => e.isActive !== false).length;
  const terminated = emps.filter(e => e.isActive === false).length;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-4 justify-between">
        <div className="flex gap-4 text-sm">
          <span className="text-neutral-600"><span className="font-semibold text-neutral-800">{active}</span> active</span>
          <span className="text-neutral-400"><span className="font-semibold">{terminated}</span> terminated</span>
        </div>
        <label className="flex items-center gap-2 text-sm text-neutral-600 cursor-pointer select-none">
          <input type="checkbox" checked={showAll} onChange={e => setShowAll(e.target.checked)} className="rounded" />
          Include terminated
        </label>
      </div>
      <div className="overflow-x-auto rounded-xl border border-neutral-200">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-neutral-50 border-b border-neutral-200">
              {['Name', 'Role', 'Position / Dept', 'Pay', 'Email', 'Hire Date', 'Status'].map(h => (
                <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-neutral-500 uppercase tracking-wide whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-100">
            {displayed.map(e => {
              const name = e.firstName ? `${e.firstName} ${e.lastName || ''}`.trim() : e.name || '—';
              const rate = e.payType === 'salary'
                ? `$${(e.annualSalary || 0).toLocaleString()}/yr`
                : `$${(e.hourlyRate || 0).toFixed(2)}/hr`;
              const hireDate = e.hireDate?.seconds
                ? new Date(e.hireDate.seconds * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                : e.hireDate || '—';
              return (
                <tr key={e.id} className={`hover:bg-neutral-50 ${e.isActive === false ? 'opacity-50' : ''}`}>
                  <td className="px-4 py-3 font-medium text-neutral-800 whitespace-nowrap">{name}</td>
                  <td className="px-4 py-3 text-neutral-500 capitalize">{e.role || '—'}</td>
                  <td className="px-4 py-3 text-neutral-500">{[e.position, e.department].filter(Boolean).join(' · ') || '—'}</td>
                  <td className="px-4 py-3 text-neutral-600 tabular-nums">{rate}</td>
                  <td className="px-4 py-3 text-neutral-400 truncate max-w-[160px]">{e.email || '—'}</td>
                  <td className="px-4 py-3 text-neutral-400 whitespace-nowrap">{hireDate}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${e.isActive === false ? 'bg-red-50 text-red-600' : 'bg-emerald-50 text-emerald-700'}`}>
                      {e.isActive === false ? 'Terminated' : 'Active'}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {displayed.length === 0 && <NoData text="No employees found." />}
      </div>
      <ExportBtn onClick={handleExport} label="Export Roster CSV" />
    </div>
  );
};

// HR-2: Termination Log
const HRTerminationLog = ({ restaurantId }) => {
  const [emps, setEmps] = useState(null);

  useEffect(() => {
    getDocs(query(
      collection(db, 'restaurants_data', restaurantId, 'employees'),
      where('isActive', '==', false),
    ))
      .then(snap => setEmps(snap.docs.map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => {
          const at = a.terminationDate?.seconds || 0;
          const bt = b.terminationDate?.seconds || 0;
          return bt - at;
        })))
      .catch(() => setEmps([]));
  }, [restaurantId]);

  const handleExport = () => {
    if (!emps?.length) return;
    downloadCSV([
      ['Name', 'Role', 'Position', 'Termination Date', 'Last Day Worked', 'Type', 'Note', 'Rehire Eligible', 'Rehire Note', 'Final Pay Issued', 'PTO Payout', 'Processed By'],
      ...emps.map(e => {
        const name    = e.firstName ? `${e.firstName} ${e.lastName || ''}`.trim() : e.name || '';
        const termDt  = e.terminationDate?.seconds ? new Date(e.terminationDate.seconds * 1000).toLocaleDateString() : '';
        return [
          name, e.role || '', e.position || '', termDt,
          e.lastDayWorked || '', e.terminationType || '', e.terminationNote || '',
          e.rehireEligible === false ? 'No' : 'Yes', e.rehireNote || '',
          e.finalPayProcessed ? 'Yes' : 'No',
          e.ptoPayout > 0 ? `$${Number(e.ptoPayout).toFixed(2)}` : '$0.00',
          e.terminatedBy || '',
        ];
      }),
    ], `termination_log_${new Date().toISOString().slice(0, 10)}.csv`);
  };

  if (!emps) return <Spinner />;

  return (
    <div className="space-y-4">
      {emps.length === 0 ? (
        <NoData text="No terminated employees on record." />
      ) : (
        <div className="overflow-x-auto rounded-xl border border-neutral-200">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-neutral-50 border-b border-neutral-200">
                {['Name', 'Termination Date', 'Last Day', 'Type', 'Rehire', 'Final Pay', 'PTO Out'].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-neutral-500 uppercase tracking-wide whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {emps.map(e => {
                const name   = e.firstName ? `${e.firstName} ${e.lastName || ''}`.trim() : e.name || '—';
                const termDt = e.terminationDate?.seconds
                  ? new Date(e.terminationDate.seconds * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                  : '—';
                return (
                  <tr key={e.id} className="hover:bg-neutral-50">
                    <td className="px-4 py-3 font-medium text-neutral-800 whitespace-nowrap">{name}</td>
                    <td className="px-4 py-3 text-neutral-500 whitespace-nowrap">{termDt}</td>
                    <td className="px-4 py-3 text-neutral-400 whitespace-nowrap">{e.lastDayWorked || '—'}</td>
                    <td className="px-4 py-3 text-neutral-500 capitalize">{(e.terminationType || '').replace('_', ' ') || '—'}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${e.rehireEligible === false ? 'bg-red-50 text-red-500' : 'bg-emerald-50 text-emerald-600'}`}>
                        {e.rehireEligible === false ? 'No' : 'Yes'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${e.finalPayProcessed ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-600'}`}>
                        {e.finalPayProcessed ? 'Issued' : 'Pending'}
                      </span>
                    </td>
                    <td className="px-4 py-3 tabular-nums text-neutral-500">
                      {e.ptoPayout > 0 ? fmt$(e.ptoPayout) : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {emps.length > 0 && <ExportBtn onClick={handleExport} label={`Export Termination Log (${emps.length})`} />}
    </div>
  );
};

// HR-3: New Hire Report
const HRNewHireReport = ({ restaurantId }) => {
  const [year, setYear]   = useState(CURRENT_YEAR);
  const [emps, setEmps]   = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    const start = Timestamp.fromDate(new Date(year, 0, 1));
    const end   = Timestamp.fromDate(new Date(year + 1, 0, 1));
    getDocs(query(
      collection(db, 'restaurants_data', restaurantId, 'employees'),
      where('createdAt', '>=', start),
      where('createdAt', '<',  end),
    ))
      .then(snap => setEmps(snap.docs.map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (a.createdAt?.seconds || 0) - (b.createdAt?.seconds || 0))))
      .catch(() => setEmps([]))
      .finally(() => setLoading(false));
  }, [restaurantId, year]);

  const handleExport = () => {
    if (!emps?.length) return;
    downloadCSV([
      ['Name', 'Role', 'Position', 'Department', 'Pay Type', 'Rate / Salary', 'Email', 'Phone', 'Date Added', 'Status'],
      ...emps.map(e => {
        const name = e.firstName ? `${e.firstName} ${e.lastName || ''}`.trim() : e.name || '';
        const rate = e.payType === 'salary'
          ? `$${(e.annualSalary || 0).toLocaleString()} / yr`
          : `$${(e.hourlyRate || 0).toFixed(2)} / hr`;
        const dt   = e.createdAt?.seconds ? new Date(e.createdAt.seconds * 1000).toLocaleDateString() : '';
        return [name, e.role || '', e.position || '', e.department || '', e.payType || '', rate,
                e.email || '', e.phone || '', dt, e.isActive === false ? 'Terminated' : 'Active'];
      }),
    ], `new_hires_${year}.csv`);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <YearPicker year={year} onChange={y => { setYear(y); setEmps(null); }} />
        <p className="text-xs text-neutral-400 max-w-xs">Most states require new hire reporting to state agencies within 20 days. SSN is required for state filings — collect directly from the employee.</p>
      </div>
      {loading || !emps ? <Spinner /> : emps.length === 0 ? (
        <NoData text={`No new employees found for ${year}.`} />
      ) : (
        <div className="overflow-x-auto rounded-xl border border-neutral-200">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-neutral-50 border-b border-neutral-200">
                {['Name', 'Role', 'Position / Dept', 'Pay', 'Email', 'Date Added'].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-neutral-500 uppercase tracking-wide whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {emps.map(e => {
                const name = e.firstName ? `${e.firstName} ${e.lastName || ''}`.trim() : e.name || '—';
                const rate = e.payType === 'salary'
                  ? `$${(e.annualSalary || 0).toLocaleString()}/yr`
                  : `$${(e.hourlyRate || 0).toFixed(2)}/hr`;
                const dt   = e.createdAt?.seconds
                  ? new Date(e.createdAt.seconds * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                  : '—';
                return (
                  <tr key={e.id} className="hover:bg-neutral-50">
                    <td className="px-4 py-3 font-medium text-neutral-800 whitespace-nowrap">{name}</td>
                    <td className="px-4 py-3 text-neutral-500 capitalize">{e.role || '—'}</td>
                    <td className="px-4 py-3 text-neutral-500">{[e.position, e.department].filter(Boolean).join(' · ') || '—'}</td>
                    <td className="px-4 py-3 tabular-nums text-neutral-600">{rate}</td>
                    <td className="px-4 py-3 text-neutral-400 truncate max-w-[160px]">{e.email || '—'}</td>
                    <td className="px-4 py-3 text-neutral-400 whitespace-nowrap">{dt}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {emps && emps.length > 0 && <ExportBtn onClick={handleExport} label={`Export New Hire Report (${emps.length})`} />}
    </div>
  );
};

// HR-4 & 7: Annual Earnings Report (also used for W-2 prep export)
const HRAnnualEarningsReport = ({ restaurantId, w2Mode = false }) => {
  const [year, setYear]     = useState(CURRENT_YEAR);
  const [data, setData]     = useState(null);
  const [empMap, setEmpMap] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    getDocs(collection(db, 'restaurants_data', restaurantId, 'employees'))
      .then(snap => {
        const m = {};
        snap.docs.forEach(d => { m[d.id] = { id: d.id, ...d.data() }; });
        setEmpMap(m);
      })
      .catch(() => {});
  }, [restaurantId]);

  useEffect(() => {
    setLoading(true);
    setData(null);
    fetchAnnualLedgerData(restaurantId, year)
      .then(({ entries }) => setData(aggregateAnnualLedger(entries, empMap)))
      .catch(() => setData([]))
      .finally(() => setLoading(false));
  }, [restaurantId, year, empMap]); // eslint-disable-line react-hooks/exhaustive-deps

  const totals = useMemo(() => {
    if (!data) return {};
    return {
      wages: data.reduce((s, e) => s + e.baseAmount + e.otPremium, 0),
      tips:  data.reduce((s, e) => s + e.tipAmount, 0),
      comm:  data.reduce((s, e) => s + e.commAmount, 0),
      bonus: data.reduce((s, e) => s + e.bonusAmount, 0),
      gross: data.reduce((s, e) => s + e.grossTotal, 0),
      hours: data.reduce((s, e) => s + e.hours + e.otHours, 0),
    };
  }, [data]);

  const handleExport = () => {
    if (!data?.length) return;
    const hdrs = w2Mode
      ? ['Employee', 'Position', 'Role', 'Total Hours', 'Regular Wages (W-2 Box 1 component)', 'OT Premium', 'Total Wages', 'Reported Tips (W-2 Box 7)', 'Commission', 'Adjustments', 'Gross Earnings', 'Note']
      : ['Employee', 'Position', 'Role', 'Regular Hours', 'OT Hours', 'Base Wages', 'OT Premium', 'Total Wages', 'Tips', 'Commission', 'Adjustments / Bonuses', 'Gross Total'];
    const rows = data.map(e => w2Mode
      ? [e.name, e.position, e.role, (e.hours + e.otHours).toFixed(1), e.baseAmount.toFixed(2), e.otPremium.toFixed(2), (e.baseAmount + e.otPremium).toFixed(2), e.tipAmount.toFixed(2), e.commAmount.toFixed(2), e.bonusAmount.toFixed(2), e.grossTotal.toFixed(2), 'Verify SSN and withholding with payroll processor']
      : [e.name, e.position, e.role, e.hours.toFixed(1), e.otHours.toFixed(1), e.baseAmount.toFixed(2), e.otPremium.toFixed(2), (e.baseAmount + e.otPremium).toFixed(2), e.tipAmount.toFixed(2), e.commAmount.toFixed(2), e.bonusAmount.toFixed(2), e.grossTotal.toFixed(2)]
    );
    const totRow = w2Mode
      ? ['TOTALS', '', '', (totals.hours).toFixed(1), '', '', totals.wages.toFixed(2), totals.tips.toFixed(2), totals.comm.toFixed(2), totals.bonus.toFixed(2), totals.gross.toFixed(2), '']
      : ['TOTALS', '', '', totals.hours.toFixed(1), '', '', '', totals.wages.toFixed(2), totals.tips.toFixed(2), totals.comm.toFixed(2), totals.bonus.toFixed(2), totals.gross.toFixed(2)];
    downloadCSV(
      [hdrs, ...rows, totRow],
      w2Mode ? `w2_prep_${year}.csv` : `annual_earnings_${year}.csv`
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <YearPicker year={year} onChange={y => { setYear(y); setData(null); }} />
        {w2Mode && (
          <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 max-w-sm">
            W-2s must be filed by Jan 31. SSN, withholding, and employer tax amounts are required — provide this CSV to your payroll processor or accountant.
          </div>
        )}
      </div>

      {loading || !data ? <Spinner /> : data.length === 0 ? (
        <NoData text={`No payroll data found for ${year}.`} />
      ) : (
        <>
          {/* Totals row */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <StatCard label="Total Wages" value={fmt$(totals.wages)} />
            <StatCard label="Total Tips" value={fmt$(totals.tips)} />
            <StatCard label="Gross Payroll" value={fmt$(totals.gross)} accent="text-neutral-900" />
            <StatCard label="Total Hours" value={fmtHrs(totals.hours)} />
          </div>

          <div className="overflow-x-auto rounded-xl border border-neutral-200">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-neutral-50 border-b border-neutral-200">
                  {['Employee', 'Position', 'Hours', 'Wages', 'Tips', 'Commission', 'Gross'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-neutral-500 uppercase tracking-wide whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100">
                {data.map(e => (
                  <tr key={e.id} className="hover:bg-neutral-50">
                    <td className="px-4 py-3 font-medium text-neutral-800 whitespace-nowrap">{e.name}</td>
                    <td className="px-4 py-3 text-neutral-500">{e.position || e.role || '—'}</td>
                    <td className="px-4 py-3 tabular-nums text-neutral-500">{fmtHrs(e.hours + e.otHours)}</td>
                    <td className="px-4 py-3 tabular-nums text-neutral-600">{fmt$(e.baseAmount + e.otPremium)}</td>
                    <td className="px-4 py-3 tabular-nums text-neutral-600">{fmt$(e.tipAmount)}</td>
                    <td className="px-4 py-3 tabular-nums text-neutral-400">{fmt$(e.commAmount)}</td>
                    <td className="px-4 py-3 tabular-nums font-semibold text-neutral-800">{fmt$(e.grossTotal)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-neutral-50 border-t-2 border-neutral-200">
                  <td className="px-4 py-3 font-semibold text-neutral-700" colSpan={2}>Total</td>
                  <td className="px-4 py-3 tabular-nums font-semibold text-neutral-700">{fmtHrs(totals.hours)}</td>
                  <td className="px-4 py-3 tabular-nums font-semibold text-neutral-700">{fmt$(totals.wages)}</td>
                  <td className="px-4 py-3 tabular-nums font-semibold text-neutral-700">{fmt$(totals.tips)}</td>
                  <td className="px-4 py-3 tabular-nums font-semibold text-neutral-700">{fmt$(totals.comm)}</td>
                  <td className="px-4 py-3 tabular-nums font-semibold text-neutral-800">{fmt$(totals.gross)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
          <ExportBtn onClick={handleExport} label={w2Mode ? `Download W-2 Prep CSV (${year})` : `Export Annual Earnings CSV (${year})`} />
        </>
      )}
    </div>
  );
};

// HR-5: Annual Tip Summary
const HRTipSummaryReport = ({ restaurantId }) => {
  const [year, setYear]     = useState(CURRENT_YEAR);
  const [data, setData]     = useState(null);
  const [empMap, setEmpMap] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    getDocs(collection(db, 'restaurants_data', restaurantId, 'employees'))
      .then(snap => {
        const m = {};
        snap.docs.forEach(d => { m[d.id] = { id: d.id, ...d.data() }; });
        setEmpMap(m);
      }).catch(() => {});
  }, [restaurantId]);

  useEffect(() => {
    setLoading(true);
    setData(null);
    fetchAnnualLedgerData(restaurantId, year)
      .then(({ entries }) => {
        const agg = aggregateAnnualLedger(entries, empMap);
        setData(agg.filter(e => e.tipAmount > 0).sort((a, b) => b.tipAmount - a.tipAmount));
      })
      .catch(() => setData([]))
      .finally(() => setLoading(false));
  }, [restaurantId, year, empMap]); // eslint-disable-line react-hooks/exhaustive-deps

  const totalTips = data ? data.reduce((s, e) => s + e.tipAmount, 0) : 0;
  const totalHours = data ? data.reduce((s, e) => s + e.hours, 0) : 0;

  const handleExport = () => {
    if (!data?.length) return;
    downloadCSV([
      ['Employee', 'Position', 'Role', 'Regular Hours', 'Total Tips Reported', '% of All Tips', 'Tips Per Hour'],
      ...data.map(e => [
        e.name, e.position, e.role, e.hours.toFixed(1),
        e.tipAmount.toFixed(2),
        totalTips > 0 ? ((e.tipAmount / totalTips) * 100).toFixed(1) + '%' : '0%',
        e.hours > 0 ? (e.tipAmount / e.hours).toFixed(2) : '0.00',
      ]),
      ['TOTALS', '', '', totalHours.toFixed(1), totalTips.toFixed(2), '100%', totalHours > 0 ? (totalTips / totalHours).toFixed(2) : '0.00'],
    ], `tip_summary_${year}.csv`);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <YearPicker year={year} onChange={y => { setYear(y); setData(null); }} />
        <div className="text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 max-w-sm">
          IRS Form 8027 is required for restaurants with 10+ tipped employees. Provide this data to your accountant each January.
        </div>
      </div>

      {loading || !data ? <Spinner /> : data.length === 0 ? (
        <NoData text={`No tip data found for ${year}.`} />
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <StatCard label={`Total Tips ${year}`} value={fmt$(totalTips)} accent="text-emerald-600" />
            <StatCard label="Tipped Employees" value={data.length} />
            <StatCard label="Avg Tips / Hour" value={fmt$(totalHours > 0 ? totalTips / totalHours : 0)} />
          </div>

          <div className="overflow-x-auto rounded-xl border border-neutral-200">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-neutral-50 border-b border-neutral-200">
                  {['Employee', 'Position', 'Hours', 'Total Tips', '% of Tips', '$/hr'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-neutral-500 uppercase tracking-wide whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100">
                {data.map(e => (
                  <tr key={e.id} className="hover:bg-neutral-50">
                    <td className="px-4 py-3 font-medium text-neutral-800 whitespace-nowrap">{e.name}</td>
                    <td className="px-4 py-3 text-neutral-500">{e.position || '—'}</td>
                    <td className="px-4 py-3 tabular-nums text-neutral-500">{fmtHrs(e.hours)}</td>
                    <td className="px-4 py-3 tabular-nums font-semibold text-emerald-700">{fmt$(e.tipAmount)}</td>
                    <td className="px-4 py-3 tabular-nums text-neutral-500">
                      {totalTips > 0 ? fmtPct((e.tipAmount / totalTips) * 100) : '0%'}
                    </td>
                    <td className="px-4 py-3 tabular-nums text-neutral-400">
                      {e.hours > 0 ? fmt$(e.tipAmount / e.hours) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-neutral-50 border-t-2 border-neutral-200">
                  <td className="px-4 py-3 font-semibold text-neutral-700" colSpan={2}>Total</td>
                  <td className="px-4 py-3 tabular-nums font-semibold text-neutral-700">{fmtHrs(totalHours)}</td>
                  <td className="px-4 py-3 tabular-nums font-semibold text-emerald-700">{fmt$(totalTips)}</td>
                  <td className="px-4 py-3 tabular-nums font-semibold text-neutral-700">100%</td>
                  <td className="px-4 py-3 tabular-nums font-semibold text-neutral-700">
                    {totalHours > 0 ? fmt$(totalTips / totalHours) : '—'}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
          <ExportBtn onClick={handleExport} label={`Export Tip Summary CSV (${year})`} />
        </>
      )}
    </div>
  );
};

// HR-6: FICA Tip Credit (Form 8846 prep)
const HRFicaCreditReport = ({ restaurantId }) => {
  const [year, setYear]     = useState(CURRENT_YEAR);
  const [data, setData]     = useState(null);
  const [empMap, setEmpMap] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    getDocs(collection(db, 'restaurants_data', restaurantId, 'employees'))
      .then(snap => {
        const m = {};
        snap.docs.forEach(d => { m[d.id] = { id: d.id, ...d.data() }; });
        setEmpMap(m);
      }).catch(() => {});
  }, [restaurantId]);

  useEffect(() => {
    setLoading(true);
    setData(null);
    fetchAnnualLedgerData(restaurantId, year)
      .then(({ entries }) => {
        const agg = aggregateAnnualLedger(entries, empMap);
        const rows = agg
          .filter(e => e.tipAmount > 0 && e.hours > 0)
          .map(e => {
            // FICA Tip Credit (IRC §45B / Form 8846):
            // Credit = (tips - tips used to bring wages to federal minimum wage) × 7.65%
            // Tips used for minimum wage = max(0, (hours × FEDERAL_MIN_WAGE) - base wages paid)
            const minWageThreshold  = e.hours * FEDERAL_MIN_WAGE;
            const tipsUsedForMinWage = Math.max(0, minWageThreshold - e.baseAmount);
            const eligibleTips       = Math.max(0, e.tipAmount - tipsUsedForMinWage);
            const ficaCredit         = eligibleTips * 0.0765;
            const effectiveHourlyRate = e.hours > 0 ? e.baseAmount / e.hours : 0;
            return { ...e, effectiveHourlyRate, tipsUsedForMinWage, eligibleTips, ficaCredit };
          })
          .sort((a, b) => b.ficaCredit - a.ficaCredit);
        setData(rows);
      })
      .catch(() => setData([]))
      .finally(() => setLoading(false));
  }, [restaurantId, year, empMap]); // eslint-disable-line react-hooks/exhaustive-deps

  const totalCredit      = data ? data.reduce((s, e) => s + e.ficaCredit, 0) : 0;
  const totalEligibleTips = data ? data.reduce((s, e) => s + e.eligibleTips, 0) : 0;

  const handleExport = () => {
    if (!data?.length) return;
    downloadCSV([
      ['Employee', 'Position', 'Hours', 'Effective Hourly Rate', 'Total Tips', 'Tips Used for Min Wage', 'Eligible Tips', 'FICA Credit (7.65%)'],
      ...data.map(e => [
        e.name, e.position, e.hours.toFixed(1),
        e.effectiveHourlyRate.toFixed(2),
        e.tipAmount.toFixed(2), e.tipsUsedForMinWage.toFixed(2),
        e.eligibleTips.toFixed(2), e.ficaCredit.toFixed(2),
      ]),
      ['TOTALS', '', '', '', '', '', totalEligibleTips.toFixed(2), totalCredit.toFixed(2)],
    ], `fica_tip_credit_${year}.csv`);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <YearPicker year={year} onChange={y => { setYear(y); setData(null); }} />
        <div className="text-xs text-purple-700 bg-purple-50 border border-purple-200 rounded-lg px-3 py-2 max-w-sm">
          IRC §45B credit: 7.65% of tips above federal minimum wage (${FEDERAL_MIN_WAGE}/hr). File on Form 8846 with your tax return. <strong>Verify with your accountant.</strong>
        </div>
      </div>

      {loading || !data ? <Spinner /> : data.length === 0 ? (
        <NoData text={`No eligible tip data found for ${year}.`} />
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <StatCard label="Estimated Credit" value={fmt$(totalCredit)} accent="text-purple-700" sub="Verify with accountant" />
            <StatCard label="Eligible Tips" value={fmt$(totalEligibleTips)} />
            <StatCard label="Employees" value={data.length} sub="with tip credit" />
          </div>

          <div className="overflow-x-auto rounded-xl border border-neutral-200">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-neutral-50 border-b border-neutral-200">
                  {['Employee', 'Position', 'Hours', 'Eff. Rate/hr', 'Total Tips', 'Used for Min Wage', 'Eligible Tips', 'Credit'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-neutral-500 uppercase tracking-wide whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100">
                {data.map(e => (
                  <tr key={e.id} className="hover:bg-neutral-50">
                    <td className="px-4 py-3 font-medium text-neutral-800 whitespace-nowrap">{e.name}</td>
                    <td className="px-4 py-3 text-neutral-500">{e.position || '—'}</td>
                    <td className="px-4 py-3 tabular-nums text-neutral-500">{fmtHrs(e.hours)}</td>
                    <td className="px-4 py-3 tabular-nums text-neutral-400">{fmt$(e.effectiveHourlyRate)}</td>
                    <td className="px-4 py-3 tabular-nums text-neutral-600">{fmt$(e.tipAmount)}</td>
                    <td className="px-4 py-3 tabular-nums text-neutral-400">{fmt$(e.tipsUsedForMinWage)}</td>
                    <td className="px-4 py-3 tabular-nums text-emerald-700 font-medium">{fmt$(e.eligibleTips)}</td>
                    <td className="px-4 py-3 tabular-nums font-semibold text-purple-700">{fmt$(e.ficaCredit)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-neutral-50 border-t-2 border-neutral-200">
                  <td className="px-4 py-3 font-semibold text-neutral-700" colSpan={6}>Total</td>
                  <td className="px-4 py-3 tabular-nums font-semibold text-emerald-700">{fmt$(totalEligibleTips)}</td>
                  <td className="px-4 py-3 tabular-nums font-bold text-purple-700">{fmt$(totalCredit)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
          <ExportBtn onClick={handleExport} label={`Export Form 8846 Prep CSV (${year})`} />
        </>
      )}
    </div>
  );
};

// 8–10. Export Panels
const ExportPayrollPanel = ({ aggregated, periods, selectedPeriodId, onPeriodChange, loading }) => {
  const handleExport = () => {
    const hdrs = ['Employee', 'Position', 'Dept', 'Hours', 'OT Hours', 'Base Pay', 'OT Premium', 'Tips', 'Commission', 'Adjustment', 'Gross Total'];
    const rows = aggregated.map(e => [
      e.name, e.position, e.dept, e.hours.toFixed(2), e.otHours.toFixed(2),
      e.baseAmount.toFixed(2), e.otPremium.toFixed(2), e.tipAmount.toFixed(2),
      e.commAmount.toFixed(2), e.bonusAmount.toFixed(2), e.grossTotal.toFixed(2),
    ]);
    const tots = ['TOTAL', '', '',
      aggregated.reduce((s, e) => s + e.hours, 0).toFixed(2),
      aggregated.reduce((s, e) => s + e.otHours, 0).toFixed(2),
      aggregated.reduce((s, e) => s + e.baseAmount, 0).toFixed(2),
      aggregated.reduce((s, e) => s + e.otPremium, 0).toFixed(2),
      aggregated.reduce((s, e) => s + e.tipAmount, 0).toFixed(2),
      aggregated.reduce((s, e) => s + e.commAmount, 0).toFixed(2),
      aggregated.reduce((s, e) => s + e.bonusAmount, 0).toFixed(2),
      aggregated.reduce((s, e) => s + e.grossTotal, 0).toFixed(2),
    ];
    downloadCSV([hdrs, ...rows, tots], `payroll_${selectedPeriodId}.csv`);
  };

  return (
    <div className="bg-white border border-neutral-200 rounded-xl p-6 space-y-5">
      <div>
        <h4 className="text-sm font-semibold text-neutral-800 mb-1">Payroll Summary Export</h4>
        <p className="text-sm text-neutral-400 mb-2">Labor cost breakdown per employee — useful for internal review and analysis.</p>
        <div className="flex items-start gap-2.5 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5">
          <span className="text-amber-500 text-sm shrink-0 mt-0.5">ℹ</span>
          <p className="text-xs text-amber-800 leading-relaxed">
            <span className="font-semibold">Submitting payroll to Gusto, ADP, or Paychex?</span> Use the full submission CSV in the <span className="font-semibold">Payroll tab</span> — lock the period, then tap "Download Payroll CSV." That file includes email, FICA tip credit, net-to-pay, and all fields payroll software needs.
          </p>
        </div>
      </div>
      <div>
        <label className="block text-xs font-semibold text-neutral-400 uppercase tracking-wide mb-1.5">Pay Period</label>
        <PeriodPicker periods={periods} selectedId={selectedPeriodId} onChange={onPeriodChange} loading={loading.periods} />
      </div>
      {aggregated.length > 0 && (
        <div className="border border-neutral-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 border-b border-neutral-100">
              <tr>
                {['Employee', 'Hours', 'Base', 'Tips', 'Gross'].map(h => (
                  <th key={h} className={`px-4 py-2.5 font-semibold text-neutral-500 ${h === 'Employee' ? 'text-left' : 'text-right'}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {aggregated.slice(0, 5).map(e => (
                <tr key={e.id} className="border-b border-neutral-100 last:border-0">
                  <td className="px-4 py-2.5 font-medium text-neutral-700">{e.name}</td>
                  <td className="px-4 py-2.5 text-right text-neutral-500 tabular-nums">{fmtHrs(e.hours)}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums">{fmt$(e.baseAmount)}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums">{fmt$(e.tipAmount)}</td>
                  <td className="px-4 py-2.5 text-right font-semibold tabular-nums">{fmt$(e.grossTotal)}</td>
                </tr>
              ))}
              {aggregated.length > 5 && (
                <tr><td colSpan={5} className="px-4 py-2 text-xs text-neutral-400 text-center">+{aggregated.length - 5} more rows in full export</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      <button onClick={handleExport} disabled={!aggregated.length}
        className="w-full px-5 py-3 text-sm font-semibold bg-neutral-900 text-white rounded-xl hover:bg-neutral-700 disabled:opacity-40 transition-colors">
        Download Payroll CSV
      </button>
    </div>
  );
};

const ExportHoursPanel = ({ aggregated, periods, selectedPeriodId, onPeriodChange, loading }) => {
  const handleExport = () => downloadCSV([
    ['Employee', 'Position', 'Department', 'Reg Hours', 'OT Hours', 'Total Hours', 'OT Cost'],
    ...aggregated.map(e => [e.name, e.position, e.dept,
      Math.max(0, e.hours - e.otHours).toFixed(2), e.otHours.toFixed(2),
      e.hours.toFixed(2), e.otPremium.toFixed(2)]),
  ], `hours_overtime_${selectedPeriodId}.csv`);

  return (
    <div className="bg-white border border-neutral-200 rounded-xl p-6 space-y-5">
      <div>
        <h4 className="text-sm font-semibold text-neutral-800 mb-1">Hours & Overtime Export</h4>
        <p className="text-sm text-neutral-400">Hours and overtime per employee. Useful for compliance reviews and HR records.</p>
      </div>
      <div>
        <label className="block text-xs font-semibold text-neutral-400 uppercase tracking-wide mb-1.5">Pay Period</label>
        <PeriodPicker periods={periods} selectedId={selectedPeriodId} onChange={onPeriodChange} loading={loading.periods} />
      </div>
      <button onClick={handleExport} disabled={!aggregated.length}
        className="w-full px-5 py-3 text-sm font-semibold bg-neutral-900 text-white rounded-xl hover:bg-neutral-700 disabled:opacity-40 transition-colors">
        Download Hours CSV
      </button>
    </div>
  );
};

const ExportPLPanel = ({ aggregated, periods, selectedPeriodId, onPeriodChange, loading }) => {
  const handleExport = () => {
    const period = periods.find(p => p.id === selectedPeriodId);
    const totalLabor = aggregated.reduce((s, e) => s + e.laborCost, 0);
    const revenue = Number(period?.revenueForPeriod || 0);
    downloadCSV([
      ['Metric', 'Amount'],
      ['Period',             fmtPeriod(selectedPeriodId)],
      ['Revenue',            revenue.toFixed(2)],
      ['Base Pay',           aggregated.reduce((s, e) => s + e.baseAmount, 0).toFixed(2)],
      ['Overtime Premium',   aggregated.reduce((s, e) => s + e.otPremium,  0).toFixed(2)],
      ['Total Labor Cost',   totalLabor.toFixed(2)],
      ['Tips Distributed',   aggregated.reduce((s, e) => s + e.tipAmount,  0).toFixed(2)],
      ['Labor % of Revenue', revenue > 0 ? fmtPct((totalLabor / revenue) * 100) : '—'],
      ['Gross After Labor',  revenue > 0 ? (revenue - totalLabor).toFixed(2) : '—'],
    ], `pl_summary_${selectedPeriodId}.csv`);
  };

  return (
    <div className="bg-white border border-neutral-200 rounded-xl p-6 space-y-5">
      <div>
        <h4 className="text-sm font-semibold text-neutral-800 mb-1">P&L Summary Export</h4>
        <p className="text-sm text-neutral-400">Revenue and labor cost summary per period — ready for accountants or bookkeepers.</p>
      </div>
      <div>
        <label className="block text-xs font-semibold text-neutral-400 uppercase tracking-wide mb-1.5">Pay Period</label>
        <PeriodPicker periods={periods} selectedId={selectedPeriodId} onChange={onPeriodChange} loading={loading.periods} />
      </div>
      <button onClick={handleExport} disabled={!aggregated.length}
        className="w-full px-5 py-3 text-sm font-semibold bg-neutral-900 text-white rounded-xl hover:bg-neutral-700 disabled:opacity-40 transition-colors">
        Download P&L CSV
      </button>
    </div>
  );
};

// ── GIFT CARDS REPORT ──────────────────────────────────────────────────────────

const GC_STATUS_COLORS = {
  sent:      { bg: 'bg-emerald-50', text: 'text-emerald-700', label: 'Active'    },
  redeemed:  { bg: 'bg-blue-50',   text: 'text-blue-700',    label: 'Redeemed'  },
  scheduled: { bg: 'bg-amber-50',  text: 'text-amber-700',   label: 'Scheduled' },
  failed:    { bg: 'bg-red-50',    text: 'text-red-700',     label: 'Failed'    },
};

const fmtDate = (ts) => {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const fmtDateTime = (ts) => {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true });
};

// ── Manager Audit Log ──────────────────────────────────────────────────────────
const ACTION_META = {
  comp:             { label: 'Comp',         color: 'bg-amber-100 text-amber-800'  },
  remove_sent_item: { label: 'Remove Item',  color: 'bg-red-100 text-red-700'     },
  void:             { label: 'Void',         color: 'bg-neutral-200 text-neutral-700' },
  refund:           { label: 'Refund',       color: 'bg-purple-100 text-purple-700'  },
};

const ManagerAuditReport = ({ restaurantId }) => {
  const [actions, setActions]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [dayRange, setDayRange]   = useState(30);
  const [typeFilter, setTypeFilter] = useState('all');

  useEffect(() => {
    setLoading(true);
    const q = query(
      collection(db, 'restaurants_data', restaurantId, 'manager_actions'),
      orderBy('createdAt', 'desc'),
      limit(500),
    );
    return onSnapshot(q, snap => {
      setActions(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, () => setLoading(false));
  }, [restaurantId]);

  const cutoff = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - dayRange);
    return d;
  }, [dayRange]);

  const inRange = useMemo(() => actions.filter(a => {
    if (!a.createdAt) return false;
    const d = a.createdAt.toDate ? a.createdAt.toDate() : new Date(a.createdAt);
    return d >= cutoff;
  }), [actions, cutoff]);

  const filtered = useMemo(() =>
    typeFilter === 'all' ? inRange : inRange.filter(a => a.type === typeFilter),
    [inRange, typeFilter]
  );

  const stats = useMemo(() => {
    const comps   = inRange.filter(a => a.type === 'comp');
    const removes = inRange.filter(a => a.type === 'remove_sent_item');
    const voids   = inRange.filter(a => a.type === 'void' || a.type === 'refund');
    const compValue  = comps.reduce((s, a)  => s + (a.item?.originalPriceCents || 0), 0);
    const voidValue  = voids.reduce((s, a)  => s + (a.amountCents || 0), 0);
    return { total: inRange.length, comps: comps.length, compValue, removes: removes.length, voids: voids.length, voidValue };
  }, [inRange]);

  const handleExport = () => {
    const hdrs = ['Date / Time', 'Type', 'Authorized By', 'Role', 'Item / Payment', 'Original Amount', 'Reservation ID'];
    const rows = filtered.map(a => [
      fmtDateTime(a.createdAt),
      ACTION_META[a.type]?.label || a.type || '—',
      a.authorizedBy?.name || '—',
      a.authorizedBy?.role || '—',
      a.item?.name || (a.paymentIntentId ? `Payment ${a.paymentIntentId}` : '—'),
      a.item?.originalPriceCents != null
        ? (a.item.originalPriceCents / 100).toFixed(2)
        : a.amountCents != null ? (a.amountCents / 100).toFixed(2) : '—',
      a.reservationId || '—',
    ]);
    downloadCSV([hdrs, ...rows], `manager_audit_${new Date().toISOString().slice(0, 10)}.csv`);
  };

  if (loading) return <Spinner />;

  return (
    <div className="space-y-5">
      {/* Controls */}
      <div className="flex flex-wrap gap-4 items-center justify-between">
        <div className="flex flex-wrap gap-2 items-center">
          <span className="text-xs font-semibold text-neutral-400 uppercase tracking-wide">Period</span>
          {[7, 30, 90].map(d => (
            <button key={d} onClick={() => setDayRange(d)}
              className={`px-3 py-1.5 text-sm rounded-lg font-medium transition-colors ${dayRange === d ? 'bg-neutral-900 text-white' : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'}`}>
              {d} Days
            </button>
          ))}
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <span className="text-xs font-semibold text-neutral-400 uppercase tracking-wide">Type</span>
          {[['all', 'All'], ['comp', 'Comps'], ['remove_sent_item', 'Removes'], ['void', 'Voids'], ['refund', 'Refunds']].map(([val, label]) => (
            <button key={val} onClick={() => setTypeFilter(val)}
              className={`px-3 py-1.5 text-sm rounded-lg font-medium transition-colors ${typeFilter === val ? 'bg-neutral-900 text-white' : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'}`}>
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard label="Total Actions" value={stats.total} sub={`last ${dayRange} days`} />
        <StatCard label="Comps" value={stats.comps} sub={fmt$(stats.compValue / 100) + ' comped'} accent="text-amber-600" />
        <StatCard label="Removed Items" value={stats.removes} sub="sent to kitchen" accent="text-red-600" />
        <StatCard label="Voids / Refunds" value={stats.voids} sub={fmt$(stats.voidValue / 100) + ' reversed'} accent="text-purple-600" />
      </div>

      {!filtered.length ? (
        <NoData text={`No manager actions in the last ${dayRange} days${typeFilter !== 'all' ? ` for type "${ACTION_META[typeFilter]?.label || typeFilter}"` : ''}.`} />
      ) : (
        <>
          <div className="bg-white border border-neutral-200 rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-neutral-50 border-b border-neutral-200">
                  <tr>
                    {['Date / Time', 'Type', 'Authorized By', 'Item / Payment', 'Amount', 'Reservation'].map(h => (
                      <th key={h} className={`px-4 py-3 font-semibold text-neutral-500 whitespace-nowrap ${h === 'Amount' || h === 'Reservation' ? 'text-right' : 'text-left'}`}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(a => {
                    const meta   = ACTION_META[a.type] || { label: a.type, color: 'bg-neutral-100 text-neutral-600' };
                    const amount = a.item?.originalPriceCents != null
                      ? a.item.originalPriceCents / 100
                      : a.amountCents != null ? a.amountCents / 100 : null;
                    const itemLabel = a.item?.name || (a.paymentIntentId ? `pi_…${a.paymentIntentId.slice(-6)}` : '—');
                    return (
                      <tr key={a.id} className="border-b border-neutral-100 last:border-0 hover:bg-neutral-50">
                        <td className="px-4 py-3 text-neutral-500 whitespace-nowrap">{fmtDateTime(a.createdAt)}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${meta.color}`}>{meta.label}</span>
                        </td>
                        <td className="px-4 py-3">
                          <p className="font-medium text-neutral-800">{a.authorizedBy?.name || '—'}</p>
                          <p className="text-xs text-neutral-400 capitalize">{a.authorizedBy?.role || ''}</p>
                        </td>
                        <td className="px-4 py-3 text-neutral-700">{itemLabel}</td>
                        <td className="px-4 py-3 text-right font-semibold text-neutral-800">
                          {amount != null ? fmt$(amount) : <span className="text-neutral-300">—</span>}
                        </td>
                        <td className="px-4 py-3 text-right text-neutral-400 text-xs font-mono">
                          {a.reservationId ? a.reservationId.slice(0, 8) + '…' : '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
          <ExportBtn onClick={handleExport} label={`Export ${filtered.length} Action${filtered.length !== 1 ? 's' : ''} CSV`} />
        </>
      )}
    </div>
  );
};

const GiftCardsReport = ({ restaurantId, view = 'overview' }) => {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [dayRange, setDayRange] = useState(90);

  useEffect(() => {
    setLoading(true);
    const q = query(
      collection(db, 'restaurants_data', restaurantId, 'giftCards'),
      orderBy('createdAt', 'desc'),
      limit(500),
    );
    return onSnapshot(q, snap => {
      setCards(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, () => setLoading(false));
  }, [restaurantId]);

  const cutoff = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - dayRange);
    return d;
  }, [dayRange]);

  const realCards = useMemo(() => cards.filter(c => !c.isTest), [cards]);

  const inRange = useMemo(() => realCards.filter(c => {
    if (!c.createdAt) return false;
    const d = c.createdAt.toDate ? c.createdAt.toDate() : new Date(c.createdAt);
    return d >= cutoff;
  }), [realCards, cutoff]);

  // ── Stats ──────────────────────────────────────────────────────────────────
  const stats = useMemo(() => {
    const sold       = inRange.length;
    const soldValue  = inRange.reduce((s, c) => s + ((c.balanceCents || 0) + (c.totalRedeemedCents || 0)), 0);
    const active     = inRange.filter(c => c.deliveryStatus === 'sent' && (c.balanceCents || 0) > 0);
    const outstanding = active.reduce((s, c) => s + (c.balanceCents || 0), 0);
    const redeemed   = inRange.filter(c => c.deliveryStatus === 'redeemed').length;
    const redeemedVal = inRange.reduce((s, c) => s + (c.totalRedeemedCents || 0), 0);
    const free       = inRange.filter(c => c.isFree).length;
    return { sold, soldValue, activeCount: active.length, outstanding, redeemed, redeemedVal, free };
  }, [inRange]);

  // ── Filtered table rows ────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    if (statusFilter === 'all') return inRange;
    if (statusFilter === 'active') return inRange.filter(c => c.deliveryStatus === 'sent' && (c.balanceCents || 0) > 0);
    return inRange.filter(c => c.deliveryStatus === statusFilter);
  }, [inRange, statusFilter]);

  const handleExportCSV = () => {
    const hdrs = ['Date', 'Recipient', 'Delivery', 'Original ($)', 'Redeemed ($)', 'Balance ($)', 'Status', 'Type', 'Card ID'];
    const rows = filtered.map(c => [
      fmtDate(c.createdAt),
      c.recipientName || c.recipientEmail || c.phone || '—',
      c.deliveryMethod || '—',
      ((( c.balanceCents || 0) + (c.totalRedeemedCents || 0)) / 100).toFixed(2),
      ((c.totalRedeemedCents || 0) / 100).toFixed(2),
      ((c.balanceCents || 0) / 100).toFixed(2),
      GC_STATUS_COLORS[c.deliveryStatus]?.label || c.deliveryStatus || '—',
      c.isFree ? 'Complimentary' : 'Purchased',
      c.id,
    ]);
    downloadCSV([hdrs, ...rows], `gift_cards_${new Date().toISOString().slice(0, 10)}.csv`);
  };

  if (loading) return <Spinner />;

  if (view === 'export') {
    return (
      <div className="bg-white border border-neutral-200 rounded-xl p-6 space-y-5">
        <div>
          <h4 className="text-sm font-semibold text-neutral-800 mb-1">Gift Card Activity Export</h4>
          <p className="text-sm text-neutral-400">Full transaction log — card ID, recipient, amounts, status, and delivery method.</p>
        </div>
        <div className="flex flex-wrap gap-3 items-center">
          <label className="text-xs font-semibold text-neutral-400 uppercase tracking-wide">Date Range</label>
          {[30, 90, 365].map(d => (
            <button key={d} onClick={() => setDayRange(d)}
              className={`px-4 py-1.5 text-sm rounded-lg font-medium transition-colors ${dayRange === d ? 'bg-neutral-900 text-white' : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'}`}>
              {d === 365 ? '1 Year' : `${d} Days`}
            </button>
          ))}
        </div>
        <p className="text-sm text-neutral-500">{filtered.length} cards in range</p>
        <button onClick={handleExportCSV} disabled={!filtered.length}
          className="w-full px-5 py-3 text-sm font-semibold bg-neutral-900 text-white rounded-xl hover:bg-neutral-700 disabled:opacity-40 transition-colors">
          Download Gift Card CSV
        </button>
      </div>
    );
  }

  // ── Overview view ──────────────────────────────────────────────────────────
  return (
    <div className="space-y-6">
      {/* Date range picker */}
      <div className="flex flex-wrap gap-2 items-center">
        <span className="text-xs font-semibold text-neutral-400 uppercase tracking-wide mr-1">Period</span>
        {[30, 90, 365].map(d => (
          <button key={d} onClick={() => setDayRange(d)}
            className={`px-4 py-1.5 text-sm rounded-lg font-medium transition-colors ${dayRange === d ? 'bg-neutral-900 text-white' : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'}`}>
            {d === 365 ? '1 Year' : `${d} Days`}
          </button>
        ))}
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard label="Cards Issued" value={stats.sold} sub={`${stats.free} complimentary`} />
        <StatCard label="Total Value" value={fmt$(stats.soldValue / 100)} sub="original purchase value" accent="text-brand-blue" />
        <StatCard label="Outstanding Liability" value={fmt$(stats.outstanding / 100)} sub={`${stats.activeCount} active card${stats.activeCount !== 1 ? 's' : ''}`} accent="text-amber-600" />
        <StatCard label="Total Redeemed" value={fmt$(stats.redeemedVal / 100)} sub={`${stats.redeemed} fully redeemed`} accent="text-emerald-600" />
      </div>

      {/* Table */}
      <div className="bg-white border border-neutral-200 rounded-xl overflow-hidden">
        {/* Table toolbar */}
        <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-3 border-b border-neutral-100">
          <div className="flex gap-1.5 flex-wrap">
            {[
              { id: 'all', label: 'All' },
              { id: 'active', label: 'Active' },
              { id: 'redeemed', label: 'Redeemed' },
              { id: 'scheduled', label: 'Scheduled' },
              { id: 'failed', label: 'Failed' },
            ].map(f => (
              <button key={f.id} onClick={() => setStatusFilter(f.id)}
                className={`px-3 py-1 text-xs font-semibold rounded-full transition-colors ${statusFilter === f.id ? 'bg-neutral-900 text-white' : 'bg-neutral-100 text-neutral-500 hover:bg-neutral-200'}`}>
                {f.label}
              </button>
            ))}
          </div>
          <button onClick={handleExportCSV} disabled={!filtered.length}
            className="px-3 py-1.5 text-xs font-medium border border-neutral-300 rounded-lg text-neutral-600 hover:bg-neutral-50 disabled:opacity-40 transition-colors">
            Export CSV
          </button>
        </div>

        {filtered.length === 0 ? (
          <NoData text="No gift cards match this filter." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[640px]">
              <thead className="bg-neutral-50 border-b border-neutral-100">
                <tr>
                  {['Date', 'Recipient', 'Type', 'Original', 'Redeemed', 'Balance', 'Via', 'Status'].map(h => (
                    <th key={h} className={`px-4 py-2.5 text-xs font-semibold text-neutral-500 uppercase tracking-wide ${h === 'Date' || h === 'Recipient' || h === 'Type' ? 'text-left' : 'text-right'}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map(c => {
                  const original = (c.balanceCents || 0) + (c.totalRedeemedCents || 0);
                  const sc = GC_STATUS_COLORS[c.deliveryStatus] || { bg: 'bg-neutral-50', text: 'text-neutral-600', label: c.deliveryStatus };
                  return (
                    <tr key={c.id} className="border-b border-neutral-100 last:border-0 hover:bg-neutral-50 transition-colors">
                      <td className="px-4 py-2.5 text-neutral-600 whitespace-nowrap">{fmtDate(c.createdAt)}</td>
                      <td className="px-4 py-2.5 font-medium text-neutral-700 max-w-[160px] truncate">
                        {c.recipientName || c.recipientEmail || c.phone || '—'}
                      </td>
                      <td className="px-4 py-2.5">
                        <span className={`inline-block text-xs font-medium px-2 py-0.5 rounded ${c.isFree ? 'bg-brand-gold-light text-amber-700' : 'bg-neutral-100 text-neutral-500'}`}>
                          {c.isFree ? 'Complimentary' : 'Purchased'}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-right tabular-nums">{fmt$(original / 100)}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums text-neutral-500">{fmt$((c.totalRedeemedCents || 0) / 100)}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums font-semibold">{fmt$((c.balanceCents || 0) / 100)}</td>
                      <td className="px-4 py-2.5 text-right text-neutral-500 capitalize">{c.deliveryMethod || '—'}</td>
                      <td className="px-4 py-2.5 text-right">
                        <span className={`inline-block text-xs font-semibold px-2 py-0.5 rounded-full ${sc.bg} ${sc.text}`}>
                          {sc.label}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

// ── SMS REPORT COMPONENTS ──────────────────────────────────────────────────────

const SMSOverviewReport = ({ smsLogs, smsLoading }) => {
  const [dayRange, setDayRange] = useState(30);
  const cutoff = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - dayRange);
    return d;
  }, [dayRange]);

  const inRange = useMemo(() => {
    return smsLogs.filter(l => {
      if (!l.createdAt) return false;
      const d = l.createdAt.toDate ? l.createdAt.toDate() : new Date(l.createdAt);
      return d >= cutoff;
    });
  }, [smsLogs, cutoff]);

  const stats = useMemo(() => {
    const sent     = inRange.filter(l => l.status !== 'failed' && l.status !== 'skipped_optout');
    const failed   = inRange.filter(l => l.status === 'failed');
    const optout   = inRange.filter(l => l.status === 'skipped_optout');
    const segments = sent.reduce((s, l) => s + (l.numSegments || 1), 0);
    const mms      = sent.reduce((s, l) => s + (l.numMedia || 0), 0);
    const byType   = {};
    sent.forEach(l => {
      const k = fmtSmsType(l.type);
      if (!byType[k]) byType[k] = { count: 0, segments: 0 };
      byType[k].count    += 1;
      byType[k].segments += (l.numSegments || 1);
    });
    return { sent: sent.length, failed: failed.length, optout: optout.length, segments, mms, byType };
  }, [inRange]);

  const typeRows = useMemo(() =>
    Object.entries(stats.byType)
      .map(([type, v]) => ({ type, ...v }))
      .sort((a, b) => b.count - a.count),
    [stats.byType]
  );
  const maxCount = typeRows.length ? Math.max(...typeRows.map(r => r.count), 1) : 1;
  const deliveryRate = (stats.sent + stats.failed) > 0
    ? ((stats.sent / (stats.sent + stats.failed)) * 100).toFixed(1)
    : '—';

  const handleExport = () => {
    downloadCSV(
      [['Type', 'Messages Sent', 'Segments Billed'],
       ...typeRows.map(r => [r.type, r.count, r.segments]),
       ['TOTAL', stats.sent, stats.segments]],
      `sms_by_type_${new Date().toISOString().slice(0,10)}.csv`
    );
  };

  if (smsLoading) return <Spinner />;

  return (
    <div className="space-y-6">
      {/* Date range */}
      <div className="flex flex-wrap gap-2 items-center">
        <span className="text-xs font-semibold text-neutral-400 uppercase tracking-wide mr-1">Period</span>
        {[30, 90, 365].map(d => (
          <button key={d} onClick={() => setDayRange(d)}
            className={`px-4 py-1.5 text-sm rounded-lg font-medium transition-colors ${dayRange === d ? 'bg-neutral-900 text-white' : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'}`}>
            {d === 365 ? '1 Year' : `${d} Days`}
          </button>
        ))}
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard label="Messages Sent"    value={stats.sent.toLocaleString()} sub="excludes opt-outs & failed" />
        <StatCard label="Segments Billed"  value={stats.segments.toLocaleString()} sub="Twilio billing unit" accent="text-brand-blue" />
        <StatCard label="Delivery Rate"    value={deliveryRate === '—' ? '—' : `${deliveryRate}%`} sub={`${stats.failed} failed`} accent={parseFloat(deliveryRate) >= 95 ? 'text-emerald-600' : 'text-amber-600'} />
        <StatCard label="Opt-Outs Skipped" value={stats.optout.toLocaleString()} sub="STOP list matches" />
      </div>

      {/* MMS count if any */}
      {stats.mms > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 text-sm text-blue-800">
          {stats.mms.toLocaleString()} MMS message{stats.mms !== 1 ? 's' : ''} sent in this period — billed separately from SMS segments.
        </div>
      )}

      {/* By type breakdown */}
      {typeRows.length > 0 ? (
        <div className="bg-white border border-neutral-200 rounded-xl p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-semibold text-neutral-700">Messages by Type</h4>
            <ExportBtn onClick={handleExport} label="Export CSV" />
          </div>
          <div className="space-y-3">
            {typeRows.map((r, i) => (
              <div key={r.type} className="flex items-center gap-3">
                <div className="w-36 shrink-0 text-right">
                  <p className="text-sm font-medium text-neutral-700 leading-tight truncate">{r.type}</p>
                  <p className="text-xs text-neutral-400">{r.segments} seg.</p>
                </div>
                <div className="flex-1 bg-neutral-100 rounded-full h-3 overflow-hidden">
                  <div className="h-full rounded-full transition-all duration-500"
                    style={{ width: `${Math.max(2, (r.count / maxCount) * 100)}%`, backgroundColor: ROLE_PALETTE[i % ROLE_PALETTE.length] }} />
                </div>
                <div className="w-16 text-right text-sm font-semibold text-neutral-700 tabular-nums">{r.count.toLocaleString()}</div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <NoData text="No SMS activity in this period." />
      )}
    </div>
  );
};

const SMSTrendReport = ({ smsLogs, smsLoading }) => {
  const months = useMemo(() => {
    const map = {};
    smsLogs
      .filter(l => l.status !== 'failed' && l.status !== 'skipped_optout')
      .forEach(l => {
        const bp = l.billingPeriod || '—';
        if (!map[bp]) map[bp] = { period: bp, count: 0, segments: 0, mms: 0 };
        map[bp].count    += 1;
        map[bp].segments += (l.numSegments || 1);
        map[bp].mms      += (l.numMedia || 0);
      });
    return Object.values(map)
      .sort((a, b) => a.period.localeCompare(b.period))
      .slice(-12);
  }, [smsLogs]);

  const maxSeg = months.length ? Math.max(...months.map(m => m.segments), 1) : 1;

  const fmtBP = (bp) => {
    if (!bp || bp === '—') return bp;
    const [y, mo] = bp.split('-');
    return new Date(parseInt(y), parseInt(mo) - 1).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  };

  const handleExport = () => downloadCSV(
    [['Month', 'Messages Sent', 'Segments Billed', 'MMS'],
     ...months.map(m => [fmtBP(m.period), m.count, m.segments, m.mms])],
    `sms_trend_${new Date().toISOString().slice(0,10)}.csv`
  );

  if (smsLoading) return <Spinner />;
  if (!months.length) return <NoData text="No SMS history found." />;

  return (
    <div className="space-y-5">
      <div className="bg-white border border-neutral-200 rounded-xl p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-semibold text-neutral-700">SMS Volume — Last 12 Months</h4>
          <ExportBtn onClick={handleExport} label="Export CSV" />
        </div>
        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[420px]">
            <thead className="bg-neutral-50 border-b border-neutral-100">
              <tr>
                {['Month', 'Messages', 'Segments', 'MMS', 'Volume'].map(h => (
                  <th key={h} className={`px-4 py-2.5 text-xs font-semibold text-neutral-500 uppercase tracking-wide ${h === 'Month' ? 'text-left' : 'text-right'}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {months.map(m => (
                <tr key={m.period} className="border-b border-neutral-100 last:border-0 hover:bg-neutral-50 transition-colors">
                  <td className="px-4 py-3 font-medium text-neutral-700">{fmtBP(m.period)}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-neutral-600">{m.count.toLocaleString()}</td>
                  <td className="px-4 py-3 text-right tabular-nums font-semibold text-brand-blue">{m.segments.toLocaleString()}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-neutral-400">{m.mms || '—'}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <div className="w-24 bg-neutral-100 rounded-full h-2 overflow-hidden">
                        <div className="h-full rounded-full bg-brand-blue transition-all duration-500"
                          style={{ width: `${Math.max(2, (m.segments / maxSeg) * 100)}%` }} />
                      </div>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const SMSExportPanel = ({ smsLogs, smsLoading }) => {
  const [dayRange, setDayRange] = useState(90);

  const cutoff = useMemo(() => {
    const d = new Date(); d.setDate(d.getDate() - dayRange); return d;
  }, [dayRange]);

  const inRange = useMemo(() =>
    smsLogs.filter(l => {
      if (!l.createdAt) return false;
      const d = l.createdAt.toDate ? l.createdAt.toDate() : new Date(l.createdAt);
      return d >= cutoff;
    }),
    [smsLogs, cutoff]
  );

  const handleExport = () => {
    const hdrs = ['Date', 'Type', 'To', 'Status', 'Segments', 'MMS', 'Billing Period', 'Twilio SID'];
    const rows = inRange.map(l => {
      const d = l.createdAt?.toDate ? l.createdAt.toDate() : new Date(l.createdAt || 0);
      return [
        d.toLocaleDateString('en-US'),
        fmtSmsType(l.type),
        l.to || '—',
        l.status || '—',
        l.numSegments || 1,
        l.numMedia || 0,
        l.billingPeriod || '—',
        l.twilioSid || '—',
      ];
    });
    downloadCSV([hdrs, ...rows], `sms_log_${new Date().toISOString().slice(0,10)}.csv`);
  };

  if (smsLoading) return <Spinner />;

  return (
    <div className="bg-white border border-neutral-200 rounded-xl p-6 space-y-5">
      <div>
        <h4 className="text-sm font-semibold text-neutral-800 mb-1">SMS Log Export</h4>
        <p className="text-sm text-neutral-400">Full message log with type, recipient, status, segment count, and Twilio SID — accountant and compliance ready.</p>
      </div>
      <div className="flex flex-wrap gap-2 items-center">
        <span className="text-xs font-semibold text-neutral-400 uppercase tracking-wide mr-1">Date Range</span>
        {[30, 90, 365].map(d => (
          <button key={d} onClick={() => setDayRange(d)}
            className={`px-4 py-1.5 text-sm rounded-lg font-medium transition-colors ${dayRange === d ? 'bg-neutral-900 text-white' : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'}`}>
            {d === 365 ? '1 Year' : `${d} Days`}
          </button>
        ))}
      </div>
      <p className="text-sm text-neutral-500">{inRange.length.toLocaleString()} messages in range</p>
      <button onClick={handleExport} disabled={!inRange.length}
        className="w-full px-5 py-3 text-sm font-semibold bg-neutral-900 text-white rounded-xl hover:bg-neutral-700 disabled:opacity-40 transition-colors">
        Download SMS Log CSV
      </button>
    </div>
  );
};

// ── Shift Overview Report ──────────────────────────────────────────────────────

const fmtShiftTs = (ts) => {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};

const fmtShiftDollars = (cents) =>
  cents == null ? '—' : `$${(cents / 100).toFixed(2)}`;

const overShortClass = (cents) => {
  if (cents == null) return 'text-neutral-400';
  if (cents > 0)  return 'text-emerald-600';
  if (cents < 0)  return 'text-red-600';
  return 'text-neutral-600';
};

const OVER_SHORT_THRESHOLD_CENTS = 2000; // $20
const STALE_SHIFT_HOURS          = 16;

const ShiftOverviewReport = ({ restaurantId }) => {
  const [openShift,        setOpenShift]        = useState(null);
  const [history,          setHistory]          = useState([]);
  const [loadingHistory,   setLoadingHistory]   = useState(true);
  const [expandedId,       setExpandedId]       = useState(null);
  const [liveDrawer,       setLiveDrawer]       = useState(null);
  // detail keyed by shiftId: { loading, loaded, cashEvents, cashSales, corrections, sessions, error }
  const [detail, setDetail] = useState({});

  // ── Live open shift ──────────────────────────────────────────────────────────
  useEffect(() => {
    const q = query(
      collection(db, 'restaurants_data', restaurantId, 'shifts'),
      where('status', '==', 'open'),
    );
    return onSnapshot(q, (snap) =>
      setOpenShift(snap.empty ? null : { id: snap.docs[0].id, ...snap.docs[0].data() })
    );
  }, [restaurantId]);

  // ── Last 30 closed shifts ────────────────────────────────────────────────────
  useEffect(() => {
    setLoadingHistory(true);
    getDocs(query(
      collection(db, 'restaurants_data', restaurantId, 'shifts'),
      where('status', '==', 'closed'),
      orderBy('closedAt', 'desc'),
      limit(30),
    ))
      .then((snap) => setHistory(snap.docs.map((d) => ({ id: d.id, ...d.data() }))))
      .catch(console.error)
      .finally(() => setLoadingHistory(false));
  }, [restaurantId]);

  // ── Live drawer session for the current open shift ───────────────────────────
  useEffect(() => {
    const sessionId = openShift?.activeCashDrawerSessionId;
    if (!sessionId) { setLiveDrawer(null); return; }
    return onSnapshot(
      doc(db, 'restaurants_data', restaurantId, 'cash_drawer_sessions', sessionId),
      (snap) => setLiveDrawer(snap.exists() ? { id: snap.id, ...snap.data() } : null),
      () => setLiveDrawer(null),
    );
  }, [restaurantId, openShift?.activeCashDrawerSessionId]);

  // ── Lazy-load detail when a shift is expanded ────────────────────────────────
  const loadDetail = async (shiftId) => {
    if (detail[shiftId]?.loaded) return;
    setDetail((prev) => ({ ...prev, [shiftId]: { loading: true, loaded: false, cashEvents: [], cashSales: [], corrections: [], sessions: [] } }));
    try {
      // All drawer sessions for this shift
      const sessionsSnap = await getDocs(query(
        collection(db, 'restaurants_data', restaurantId, 'cash_drawer_sessions'),
        where('shiftId', '==', shiftId),
      ));
      const sessions = sessionsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

      // All cash events from every session (all types)
      const cashEvents = [];
      const cashSales  = [];
      for (const sDoc of sessionsSnap.docs) {
        const evSnap = await getDocs(
          collection(db, 'restaurants_data', restaurantId, 'cash_drawer_sessions', sDoc.id, 'cash_events')
        );
        evSnap.docs.forEach((d) => {
          const data = d.data();
          const ev = { id: d.id, sessionId: sDoc.id, ...data };
          if (['paid_out', 'paid_in', 'no_sale_open'].includes(data.type)) cashEvents.push(ev);
          if (['cash_sale', 'cash_tip'].includes(data.type)) cashSales.push(ev);
        });
      }
      const sortByTime = (a, b) => (a.createdAt?.toDate?.()?.getTime() || 0) - (b.createdAt?.toDate?.()?.getTime() || 0);
      cashEvents.sort(sortByTime);
      cashSales.sort(sortByTime);

      // Shift corrections from shift_events subcollection
      const eventsSnap = await getDocs(
        collection(db, 'restaurants_data', restaurantId, 'shifts', shiftId, 'shift_events')
      );
      const corrections = eventsSnap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((e) => e.type === 'shift_correction');

      setDetail((prev) => ({ ...prev, [shiftId]: { loading: false, loaded: true, cashEvents, cashSales, corrections, sessions } }));
    } catch {
      setDetail((prev) => ({ ...prev, [shiftId]: { loading: false, loaded: true, cashEvents: [], cashSales: [], corrections: [], sessions: [], error: true } }));
    }
  };

  const handleToggle = (shiftId) => {
    if (expandedId === shiftId) {
      setExpandedId(null);
    } else {
      setExpandedId(shiftId);
      loadDetail(shiftId);
    }
  };

  // ── Alerts — computed from loaded data ───────────────────────────────────────
  const alerts = useMemo(() => {
    const list = [];
    if (openShift?.openedAt) {
      const ms    = openShift.openedAt.toDate?.().getTime() || 0;
      const hours = (Date.now() - ms) / 3600000;
      if (hours > STALE_SHIFT_HOURS) {
        list.push({ id: 'stale', text: `Shift has been open for ${Math.round(hours)} hours — may have been forgotten.` });
      }
    }
    const noCount = history.filter(
      (s) => s.summary?.countedCashCents == null && (s.summary?.cashSalesCents || 0) > 0
    );
    if (noCount.length) {
      list.push({ id: 'nocount', text: `${noCount.length} closed shift${noCount.length > 1 ? 's' : ''} have no cash count recorded.` });
    }
    const flagged = history.filter(
      (s) => s.summary?.overShortCents != null && Math.abs(s.summary.overShortCents) > OVER_SHORT_THRESHOLD_CENTS
    );
    if (flagged.length) {
      list.push({ id: 'overshort', text: `${flagged.length} shift${flagged.length > 1 ? 's' : ''} with over/short greater than $20.` });
    }
    return list;
  }, [openShift, history]);

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-5 max-w-3xl">

      {/* Alerts */}
      {alerts.length > 0 && (
        <div className="space-y-2">
          {alerts.map((a) => (
            <div key={a.id} className="flex items-start gap-3 px-4 py-3 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-800">
              <span className="shrink-0 mt-0.5">⚠</span>
              <span>{a.text}</span>
            </div>
          ))}
        </div>
      )}

      {/* Current shift */}
      <div className="bg-white border border-neutral-200 rounded-xl p-5">
        <p className="text-sm font-semibold text-neutral-800 mb-3">Current Shift</p>
        {openShift ? (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse shrink-0" />
              <span className="text-sm font-semibold text-emerald-700">Open</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-2 text-sm">
              <div>
                <p className="text-xs text-neutral-400 uppercase tracking-wide mb-0.5">Opened by</p>
                <p className="font-medium text-neutral-800">{openShift.employeeName || '—'}</p>
              </div>
              <div>
                <p className="text-xs text-neutral-400 uppercase tracking-wide mb-0.5">Opened at</p>
                <p className="font-medium text-neutral-800">{fmtShiftTs(openShift.openedAt)}</p>
              </div>
              <div>
                <p className="text-xs text-neutral-400 uppercase tracking-wide mb-0.5">Drawer</p>
                <p className="font-medium text-neutral-800">
                  {openShift.activeCashDrawerSessionId ? 'Open' : 'No active drawer'}
                </p>
              </div>
            </div>

            {/* Live drawer reading */}
            {liveDrawer && (
              <div className="bg-sky-50 border border-sky-200 rounded-xl p-4">
                <p className="text-xs font-semibold text-sky-700 uppercase tracking-wide mb-3">Live Cash Drawer</p>
                <div className="flex items-end gap-3 mb-3 flex-wrap">
                  <div>
                    <p className="text-xs text-sky-600 mb-0.5">Estimated in Drawer</p>
                    <p className="text-2xl font-bold text-sky-900">{fmtShiftDollars(liveDrawer.expectedCashCents)}</p>
                  </div>
                  {liveDrawer.openingFloatCents > 0 && (
                    <div>
                      <p className="text-xs text-sky-600 mb-0.5">Opening Float</p>
                      <p className="text-base font-semibold text-sky-800">{fmtShiftDollars(liveDrawer.openingFloatCents)}</p>
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-3 sm:grid-cols-5 gap-3 text-xs">
                  {[
                    { label: 'Cash Sales',  val: fmtShiftDollars(liveDrawer.totals?.cashSalesCents),  pos: true  },
                    { label: 'Cash Tips',   val: fmtShiftDollars(liveDrawer.totals?.cashTipsCents),   pos: true  },
                    { label: 'Paid In',     val: fmtShiftDollars(liveDrawer.totals?.paidInCents),     pos: true  },
                    { label: 'Paid Out',    val: fmtShiftDollars(liveDrawer.totals?.paidOutCents),    pos: false },
                    { label: 'No-Sale Cnt', val: liveDrawer.totals?.noSaleCount ?? 0,                 pos: null  },
                  ].map(({ label, val, pos }) => (
                    <div key={label}>
                      <p className="text-sky-500 uppercase tracking-wide mb-0.5">{label}</p>
                      <p className={`font-semibold ${pos === true ? 'text-emerald-700' : pos === false ? 'text-red-600' : 'text-neutral-700'}`}>{val}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full bg-neutral-300 shrink-0" />
            <span className="text-sm text-neutral-500">No shift currently open</span>
          </div>
        )}
      </div>

      {/* Shift history */}
      <div className="bg-white border border-neutral-200 rounded-xl p-5">
        <p className="text-sm font-semibold text-neutral-800 mb-3">Shift History <span className="font-normal text-neutral-400">(last 30)</span></p>

        {loadingHistory ? (
          <p className="text-sm text-neutral-400 text-center py-8">Loading…</p>
        ) : history.length === 0 ? (
          <p className="text-sm text-neutral-400 text-center py-8">No closed shifts yet.</p>
        ) : (
          <div className="space-y-2">
            {history.map((shift) => {
              const s        = shift.summary || {};
              const overShort = s.overShortCents ?? null;
              const isOpen   = expandedId === shift.id;
              const d        = detail[shift.id];
              const noCountFlag  = s.countedCashCents == null && (s.cashSalesCents || 0) > 0;
              const bigOsFlag    = overShort != null && Math.abs(overShort) > OVER_SHORT_THRESHOLD_CENTS;

              return (
                <div key={shift.id} className="border border-neutral-100 rounded-xl overflow-hidden">
                  {/* Summary row — always visible */}
                  <button
                    className="w-full text-left p-4 hover:bg-neutral-50 transition-colors"
                    onClick={() => handleToggle(shift.id)}
                  >
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div>
                        <p className="text-sm font-semibold text-neutral-800">
                          {fmtShiftTs(shift.openedAt)}
                          <span className="text-neutral-400 font-normal mx-1.5">→</span>
                          {fmtShiftTs(shift.closedAt)}
                        </p>
                        <p className="text-xs text-neutral-400 mt-0.5">
                          Opened by <span className="font-medium text-neutral-600">{shift.employeeName || '—'}</span>
                          {shift.closedByEmployeeName && (
                            <> · Closed by <span className="font-medium text-neutral-600">{shift.closedByEmployeeName}</span></>
                          )}
                        </p>
                        {/* Inline flags */}
                        <div className="flex gap-1.5 mt-1.5 flex-wrap">
                          {noCountFlag && (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium">No cash count</span>
                          )}
                          {bigOsFlag && (
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${overShort < 0 ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'}`}>
                              {overShort > 0 ? '+' : ''}{fmtShiftDollars(overShort)} {overShort > 0 ? 'over' : 'short'}
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-4 shrink-0">
                        {/* Over/short */}
                        {overShort != null && !bigOsFlag && (
                          <span className={`text-sm font-semibold ${overShortClass(overShort)}`}>
                            {overShort >= 0 ? '+' : ''}{fmtShiftDollars(overShort)}
                            <span className="text-xs font-normal text-neutral-400 ml-1">
                              {overShort > 0 ? 'over' : overShort < 0 ? 'short' : 'exact'}
                            </span>
                          </span>
                        )}
                        <span className="text-neutral-300 text-sm">{isOpen ? '▲' : '▼'}</span>
                      </div>
                    </div>

                    {/* Quick stats row */}
                    <div className="grid grid-cols-3 sm:grid-cols-6 gap-3 mt-3 text-xs">
                      {[
                        { label: 'Card',       val: fmtShiftDollars(s.cardGrossCents)  },
                        { label: 'Cash Sales', val: fmtShiftDollars(s.cashSalesCents)  },
                        { label: 'Tips',       val: fmtShiftDollars((s.cardTipsCents || 0) + (s.cashTipsCents || 0)) },
                        { label: 'Expected',   val: fmtShiftDollars(s.expectedCashCents) },
                        { label: 'Counted',    val: fmtShiftDollars(s.countedCashCents)  },
                        { label: 'Float',      val: fmtShiftDollars(s.openingFloatCents) },
                      ].map(({ label, val }) => (
                        <div key={label}>
                          <p className="text-neutral-400 uppercase tracking-wide mb-0.5">{label}</p>
                          <p className="font-medium text-neutral-700">{val}</p>
                        </div>
                      ))}
                    </div>
                  </button>

                  {/* Expanded detail */}
                  {isOpen && (
                    <div className="border-t border-neutral-100 bg-neutral-50 px-4 py-4 space-y-5">

                      {d?.loading && <p className="text-sm text-neutral-400">Loading detail…</p>}
                      {d?.error   && <p className="text-sm text-red-500">Failed to load detail.</p>}

                      {d?.loaded && (
                        <>
                          {/* ── Cash Reconciliation Waterfall ────────────────── */}
                          <div className="bg-white border border-neutral-200 rounded-xl p-4">
                            <p className="text-xs font-semibold text-neutral-500 uppercase tracking-wide mb-3">Cash Reconciliation</p>
                            <div className="space-y-1.5 text-sm">
                              {[
                                { label: 'Opening Float',          val: s.openingFloatCents,              sign: null   },
                                { label: 'Cash Sales',             val: s.cashSalesCents,                 sign: '+'    },
                                { label: 'Cash Tips',              val: s.cashTipsCents,                  sign: '+'    },
                                ...(shift.totals?.cashServiceChargeCents > 0 ? [{ label: 'Service Charge (cash)', val: shift.totals.cashServiceChargeCents, sign: '+' }] : []),
                                ...(s.paidInCents  > 0 ? [{ label: 'Paid In',  val: s.paidInCents,  sign: '+' }] : []),
                                ...(s.paidOutCents > 0 ? [{ label: 'Paid Out', val: s.paidOutCents, sign: '−' }] : []),
                              ].map(({ label, val, sign }) => (
                                <div key={label} className="flex justify-between items-center">
                                  <span className="text-neutral-500">{label}</span>
                                  <span className={`font-medium tabular-nums ${sign === '+' ? 'text-emerald-700' : sign === '−' ? 'text-red-600' : 'text-neutral-700'}`}>
                                    {sign && sign !== null ? sign : ''}{fmtShiftDollars(val)}
                                  </span>
                                </div>
                              ))}
                              <div className="border-t border-neutral-200 pt-2 mt-1 space-y-1">
                                <div className="flex justify-between items-center font-semibold">
                                  <span className="text-neutral-700">Expected in Drawer</span>
                                  <span className="text-neutral-900 tabular-nums">{fmtShiftDollars(s.expectedCashCents)}</span>
                                </div>
                                {s.countedCashCents != null ? (
                                  <>
                                    <div className="flex justify-between items-center font-semibold">
                                      <span className="text-neutral-700">Counted</span>
                                      <span className="text-neutral-900 tabular-nums">{fmtShiftDollars(s.countedCashCents)}</span>
                                    </div>
                                    <div className="flex justify-between items-center font-bold text-base">
                                      <span className={s.overShortCents === 0 ? 'text-emerald-700' : s.overShortCents > 0 ? 'text-blue-700' : 'text-red-700'}>
                                        {s.overShortCents === 0 ? 'Exact' : s.overShortCents > 0 ? 'Over' : 'Short'}
                                      </span>
                                      <span className={`tabular-nums ${s.overShortCents === 0 ? 'text-emerald-700' : s.overShortCents > 0 ? 'text-blue-700' : 'text-red-700'}`}>
                                        {s.overShortCents >= 0 ? '+' : ''}{fmtShiftDollars(s.overShortCents)}
                                      </span>
                                    </div>
                                  </>
                                ) : (
                                  <p className="text-xs text-amber-600 italic">No cash count recorded for this shift.</p>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* ── Drawer Sessions ──────────────────────────────── */}
                          {d.sessions.length > 0 && (
                            <div>
                              <p className="text-xs font-semibold text-neutral-500 uppercase tracking-wide mb-2">Drawer Sessions</p>
                              <div className="space-y-2">
                                {d.sessions.map((sess) => (
                                  <div key={sess.id} className="bg-white border border-neutral-200 rounded-xl p-3 text-sm">
                                    <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                                      <div className="flex items-center gap-2">
                                        <span className={`w-2 h-2 rounded-full shrink-0 ${sess.status === 'open' ? 'bg-emerald-500 animate-pulse' : 'bg-neutral-300'}`} />
                                        <span className="font-semibold text-neutral-800 capitalize">{sess.drawerId || 'Main'} drawer</span>
                                        <span className="text-xs text-neutral-400">{sess.status}</span>
                                      </div>
                                      {sess.overShortCents != null && (
                                        <span className={`text-xs font-semibold tabular-nums px-2 py-0.5 rounded-full ${sess.overShortCents === 0 ? 'bg-emerald-100 text-emerald-700' : sess.overShortCents > 0 ? 'bg-blue-100 text-blue-700' : 'bg-red-100 text-red-700'}`}>
                                          {sess.overShortCents >= 0 ? '+' : ''}{fmtShiftDollars(sess.overShortCents)} {sess.overShortCents === 0 ? 'exact' : sess.overShortCents > 0 ? 'over' : 'short'}
                                        </span>
                                      )}
                                    </div>
                                    <div className="grid grid-cols-3 sm:grid-cols-5 gap-x-4 gap-y-1.5 text-xs">
                                      {[
                                        { label: 'Float',    val: fmtShiftDollars(sess.openingFloatCents)         },
                                        { label: 'Sales',    val: fmtShiftDollars(sess.totals?.cashSalesCents)    },
                                        { label: 'Tips',     val: fmtShiftDollars(sess.totals?.cashTipsCents)     },
                                        { label: 'Expected', val: fmtShiftDollars(sess.expectedCashCents)         },
                                        { label: 'Counted',  val: fmtShiftDollars(sess.countedCashCents)          },
                                      ].map(({ label, val }) => (
                                        <div key={label}>
                                          <p className="text-neutral-400 uppercase tracking-wide mb-0.5">{label}</p>
                                          <p className="font-medium text-neutral-700">{val}</p>
                                        </div>
                                      ))}
                                    </div>
                                    <div className="mt-2 text-xs text-neutral-400 space-y-0.5">
                                      <p>Opened by <span className="font-medium text-neutral-600">{sess.openedByEmployeeName || '—'}</span> · {fmtShiftTs(sess.openedAt)}</p>
                                      {sess.closedByEmployeeName && (
                                        <p>Closed by <span className="font-medium text-neutral-600">{sess.closedByEmployeeName}</span> · {fmtShiftTs(sess.closedAt)}</p>
                                      )}
                                      {sess.totals?.paidInCents  > 0 && <p className="text-emerald-600">Paid in: +{fmtShiftDollars(sess.totals.paidInCents)}</p>}
                                      {sess.totals?.paidOutCents > 0 && <p className="text-red-600">Paid out: −{fmtShiftDollars(sess.totals.paidOutCents)}</p>}
                                      {sess.totals?.noSaleCount  > 0 && <p>No-sale opens: {sess.totals.noSaleCount}</p>}
                                      {sess.closeComment && <p className="italic">Note: "{sess.closeComment}"</p>}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* ── Cash Transactions (sales + tips) ─────────────── */}
                          {d.cashSales.length > 0 && (
                            <div>
                              <p className="text-xs font-semibold text-neutral-500 uppercase tracking-wide mb-2">Cash Transactions ({d.cashSales.length})</p>
                              <div className="bg-white border border-neutral-200 rounded-xl overflow-hidden">
                                <table className="w-full text-xs">
                                  <thead>
                                    <tr className="border-b border-neutral-100 text-neutral-400">
                                      <th className="text-left px-3 py-2 font-medium uppercase tracking-wide">Time</th>
                                      <th className="text-left px-3 py-2 font-medium uppercase tracking-wide">Type</th>
                                      <th className="text-right px-3 py-2 font-medium uppercase tracking-wide">Sale</th>
                                      <th className="text-right px-3 py-2 font-medium uppercase tracking-wide">Tip</th>
                                      <th className="text-right px-3 py-2 font-medium uppercase tracking-wide">Received</th>
                                      <th className="text-right px-3 py-2 font-medium uppercase tracking-wide">Change</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {d.cashSales.map((e) => (
                                      <tr key={e.id} className="border-b border-neutral-50 hover:bg-neutral-50">
                                        <td className="px-3 py-2 text-neutral-500">{fmtShiftTs(e.createdAt)}</td>
                                        <td className="px-3 py-2">
                                          <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${e.type === 'cash_sale' ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'}`}>
                                            {e.type === 'cash_sale' ? 'Sale' : 'Tip'}
                                          </span>
                                        </td>
                                        <td className="px-3 py-2 text-right font-medium text-neutral-800 tabular-nums">{e.baseCents != null ? fmtShiftDollars(e.baseCents) : '—'}</td>
                                        <td className="px-3 py-2 text-right text-emerald-700 tabular-nums">{(e.tipCents || e.amountCents) > 0 ? `+${fmtShiftDollars(e.tipCents ?? e.amountCents)}` : '—'}</td>
                                        <td className="px-3 py-2 text-right text-neutral-600 tabular-nums">{e.receivedCents != null ? fmtShiftDollars(e.receivedCents) : '—'}</td>
                                        <td className="px-3 py-2 text-right text-neutral-500 tabular-nums">{e.changeGivenCents > 0 ? fmtShiftDollars(e.changeGivenCents) : '—'}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                  <tfoot>
                                    <tr className="border-t border-neutral-200 font-semibold text-neutral-700 bg-neutral-50">
                                      <td className="px-3 py-2" colSpan={2}>Totals</td>
                                      <td className="px-3 py-2 text-right tabular-nums">{fmtShiftDollars(d.cashSales.filter(e=>e.type==='cash_sale').reduce((acc,e)=>acc+(e.baseCents||0),0))}</td>
                                      <td className="px-3 py-2 text-right tabular-nums text-emerald-700">+{fmtShiftDollars(d.cashSales.reduce((acc,e)=>acc+(e.tipCents||e.amountCents||0),0))}</td>
                                      <td className="px-3 py-2 text-right tabular-nums">{fmtShiftDollars(d.cashSales.filter(e=>e.receivedCents!=null).reduce((acc,e)=>acc+(e.receivedCents||0),0))}</td>
                                      <td className="px-3 py-2 text-right tabular-nums">{fmtShiftDollars(d.cashSales.reduce((acc,e)=>acc+(e.changeGivenCents||0),0))}</td>
                                    </tr>
                                  </tfoot>
                                </table>
                              </div>
                            </div>
                          )}

                          {/* ── Paid Out events ───────────────────────────────── */}
                          {d.cashEvents.filter(e => e.type === 'paid_out').length > 0 && (
                            <div>
                              <p className="text-xs font-semibold text-neutral-500 uppercase tracking-wide mb-2">Paid Out</p>
                              <div className="space-y-1.5">
                                {d.cashEvents.filter(e => e.type === 'paid_out').map(e => (
                                  <div key={e.id} className="flex items-start justify-between gap-3 text-sm">
                                    <div>
                                      <p className="font-medium text-red-700">−{fmtShiftDollars(e.amountCents)}</p>
                                      <p className="text-xs text-neutral-500">
                                        {e.createdByEmployeeName || '—'} · {fmtShiftTs(e.createdAt)}
                                      </p>
                                      {e.reason && <p className="text-xs text-neutral-600 italic">"{e.reason}"</p>}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* ── Paid In events ────────────────────────────────── */}
                          {d.cashEvents.filter(e => e.type === 'paid_in').length > 0 && (
                            <div>
                              <p className="text-xs font-semibold text-neutral-500 uppercase tracking-wide mb-2">Paid In</p>
                              <div className="space-y-1.5">
                                {d.cashEvents.filter(e => e.type === 'paid_in').map(e => (
                                  <div key={e.id} className="text-sm">
                                    <p className="font-medium text-emerald-700">+{fmtShiftDollars(e.amountCents)}</p>
                                    <p className="text-xs text-neutral-500">
                                      {e.createdByEmployeeName || '—'} · {fmtShiftTs(e.createdAt)}
                                    </p>
                                    {e.reason && <p className="text-xs text-neutral-600 italic">"{e.reason}"</p>}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* ── No-sale events ────────────────────────────────── */}
                          {d.cashEvents.filter(e => e.type === 'no_sale_open').length > 0 && (
                            <div>
                              <p className="text-xs font-semibold text-neutral-500 uppercase tracking-wide mb-2">No-Sale Opens ({d.cashEvents.filter(e => e.type === 'no_sale_open').length})</p>
                              <div className="space-y-1.5">
                                {d.cashEvents.filter(e => e.type === 'no_sale_open').map(e => (
                                  <div key={e.id} className="text-sm">
                                    <p className="text-xs text-neutral-500">
                                      {e.createdByEmployeeName || '—'} · {fmtShiftTs(e.createdAt)}
                                    </p>
                                    {e.reason && <p className="text-xs text-neutral-600 italic">"{e.reason}"</p>}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* No events at all */}
                          {d.cashEvents.length === 0 && d.cashSales.length === 0 && d.corrections.length === 0 && (
                            <p className="text-xs text-neutral-400 italic">No cash events recorded for this shift.</p>
                          )}

                          {/* ── Time Corrections ─────────────────────────────── */}
                          {d.corrections.length > 0 && (
                            <div className="border-t border-neutral-200 pt-4">
                              <p className="text-xs font-semibold text-neutral-500 uppercase tracking-wide mb-2">Time Corrections</p>
                              <div className="space-y-2">
                                {d.corrections.map(c => (
                                  <div key={c.id} className="text-xs text-neutral-600 bg-white border border-neutral-200 rounded-lg px-3 py-2 space-y-0.5">
                                    <p className="font-medium text-neutral-700">Corrected by {c.correctedByEmployeeName || '—'} · {fmtShiftTs(c.createdAt)}</p>
                                    {c.newOpenedAt && (
                                      <p>Open: <span className="line-through text-neutral-400">{fmtShiftTs(c.originalOpenedAt)}</span> → {fmtShiftTs(c.newOpenedAt)}</p>
                                    )}
                                    {c.newClosedAt && (
                                      <p>Close: <span className="line-through text-neutral-400">{fmtShiftTs(c.originalClosedAt)}</span> → {fmtShiftTs(c.newClosedAt)}</p>
                                    )}
                                    {c.reason && <p className="italic">"{c.reason}"</p>}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

// ── SALES TAX REPORT ───────────────────────────────────────────────────────────
const SalesTaxReport = ({ restaurantId }) => {
  const now = new Date();
  const [startDate, setStartDate] = useState(() =>
    new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10)
  );
  const [endDate, setEndDate] = useState(() => now.toISOString().slice(0, 10));
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState(null);
  const [summary, setSummary] = useState(null);
  const [fetchError, setFetchError] = useState('');

  const applyPreset = (preset) => {
    const n = new Date();
    if (preset === 'thisMonth') {
      setStartDate(new Date(n.getFullYear(), n.getMonth(), 1).toISOString().slice(0, 10));
      setEndDate(n.toISOString().slice(0, 10));
    } else if (preset === 'lastMonth') {
      setStartDate(new Date(n.getFullYear(), n.getMonth() - 1, 1).toISOString().slice(0, 10));
      setEndDate(new Date(n.getFullYear(), n.getMonth(), 0).toISOString().slice(0, 10));
    } else if (preset === 'thisQuarter') {
      const q = Math.floor(n.getMonth() / 3);
      setStartDate(new Date(n.getFullYear(), q * 3, 1).toISOString().slice(0, 10));
      setEndDate(n.toISOString().slice(0, 10));
    } else if (preset === 'lastQuarter') {
      const q = Math.floor(n.getMonth() / 3) - 1;
      const yr = q < 0 ? n.getFullYear() - 1 : n.getFullYear();
      const qn = q < 0 ? 3 : q;
      setStartDate(new Date(yr, qn * 3, 1).toISOString().slice(0, 10));
      setEndDate(new Date(yr, qn * 3 + 3, 0).toISOString().slice(0, 10));
    } else if (preset === 'ytd') {
      setStartDate(new Date(n.getFullYear(), 0, 1).toISOString().slice(0, 10));
      setEndDate(n.toISOString().slice(0, 10));
    } else if (preset === 'lastYear') {
      const y = n.getFullYear() - 1;
      setStartDate(new Date(y, 0, 1).toISOString().slice(0, 10));
      setEndDate(new Date(y, 11, 31).toISOString().slice(0, 10));
    }
  };

  const fetchData = async () => {
    if (!startDate || !endDate) return;
    setLoading(true);
    setFetchError('');
    try {
      const startTs = Timestamp.fromDate(new Date(startDate + 'T00:00:00'));
      const endTs   = Timestamp.fromDate(new Date(endDate   + 'T23:59:59'));

      const [resSnap, pickupSnap] = await Promise.all([
        getDocs(query(
          collection(db, 'restaurants_data', restaurantId, 'reservations'),
          where('date', '>=', startTs),
          where('date', '<=', endTs),
        )),
        getDocs(query(
          collection(db, 'restaurants_data', restaurantId, 'pickup_orders'),
          where('createdAt', '>=', startTs),
          where('createdAt', '<=', endTs),
        )),
      ]);

      const byMonth = {};
      let totalTax = 0, totalTaxable = 0, totalCount = 0;

      const record = (dateObj, taxAmt, taxableAmt) => {
        const key = `${dateObj.getFullYear()}-${String(dateObj.getMonth() + 1).padStart(2, '0')}`;
        if (!byMonth[key]) byMonth[key] = { tax: 0, taxable: 0, count: 0 };
        byMonth[key].tax     += taxAmt;
        byMonth[key].taxable += taxableAmt;
        byMonth[key].count   += 1;
        totalTax     += taxAmt;
        totalTaxable += taxableAmt;
        totalCount   += 1;
      };

      resSnap.docs.forEach(d => {
        const res = d.data();
        const dateObj = res.date?.toDate ? res.date.toDate() : new Date(res.date);
        (res.payments || []).forEach(p => {
          const tc = Number(p.taxCents) || 0;
          if (tc <= 0) return;
          // payment.amount = subtotal + SC + tax; taxable = amount − tax
          const taxAmt     = tc / 100;
          const taxableAmt = Math.max(0, (Number(p.amount) || 0) - taxAmt);
          record(dateObj, taxAmt, taxableAmt);
        });
      });

      pickupSnap.docs.forEach(d => {
        const order = d.data();
        const tax     = Number(order.tax)      || 0;
        const taxable = Number(order.subtotal) || 0;
        if (tax <= 0) return;
        const dateObj = order.createdAt?.toDate ? order.createdAt.toDate() : new Date(order.createdAt);
        record(dateObj, tax, taxable);
      });

      const sortedRows = Object.entries(byMonth)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([month, v]) => ({
          month,
          tax:           v.tax,
          taxable:       v.taxable,
          count:         v.count,
          effectiveRate: v.taxable > 0 ? (v.tax / v.taxable) * 100 : 0,
        }));

      setRows(sortedRows);
      setSummary({
        totalTax,
        totalTaxable,
        totalCount,
        effectiveRate: totalTaxable > 0 ? (totalTax / totalTaxable) * 100 : 0,
      });
    } catch (e) {
      console.error('[SalesTaxReport]', e);
      setFetchError(e.message || 'Failed to load data.');
    } finally {
      setLoading(false);
    }
  };

  // Run on mount with default range
  useEffect(() => { fetchData(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fmtMonth = (key) => {
    const [y, m] = key.split('-');
    return new Date(Number(y), Number(m) - 1, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  const handleExport = () => {
    if (!rows?.length) return;
    const hdrs = ['Month', 'Taxable Sales', 'Tax Collected', 'Transactions', 'Effective Rate'];
    const dataRows = rows.map(r => [
      fmtMonth(r.month),
      r.taxable.toFixed(2),
      r.tax.toFixed(2),
      r.count,
      r.taxable > 0 ? r.effectiveRate.toFixed(3) + '%' : '—',
    ]);
    if (summary) {
      dataRows.push([]);
      dataRows.push(['TOTAL', summary.totalTaxable.toFixed(2), summary.totalTax.toFixed(2), summary.totalCount, summary.effectiveRate.toFixed(3) + '%']);
      dataRows.push([]);
      dataRows.push(['Period', `${startDate} to ${endDate}`]);
      dataRows.push(['Generated', new Date().toLocaleDateString()]);
      dataRows.push(['Note', 'You are responsible for remitting collected sales tax to your state/local authority.']);
    }
    downloadCSV([hdrs, ...dataRows], `sales_tax_${startDate}_to_${endDate}.csv`);
  };

  const PRESETS = [
    { id: 'thisMonth',    label: 'This Month'    },
    { id: 'lastMonth',    label: 'Last Month'    },
    { id: 'thisQuarter',  label: 'This Quarter'  },
    { id: 'lastQuarter',  label: 'Last Quarter'  },
    { id: 'ytd',          label: 'YTD'           },
    { id: 'lastYear',     label: 'Last Year'     },
  ];

  return (
    <div className="space-y-5">
      {/* Date controls */}
      <div className="bg-white border border-neutral-200 rounded-xl p-4 space-y-3">
        <div className="flex flex-wrap gap-2">
          {PRESETS.map(p => (
            <button key={p.id} onClick={() => applyPreset(p.id)}
              className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-neutral-100 text-neutral-600 hover:bg-neutral-200 transition-colors shrink-0">
              {p.label}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs font-medium text-neutral-500 mb-1">From</label>
            <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
              className="text-sm border border-neutral-200 rounded-lg px-3 py-1.5 focus:outline-none" />
          </div>
          <div>
            <label className="block text-xs font-medium text-neutral-500 mb-1">To</label>
            <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
              className="text-sm border border-neutral-200 rounded-lg px-3 py-1.5 focus:outline-none" />
          </div>
          <button onClick={fetchData} disabled={loading}
            className="text-sm font-semibold px-4 py-2 bg-neutral-900 text-white rounded-lg hover:opacity-80 disabled:opacity-50 transition-opacity shrink-0">
            {loading ? 'Loading…' : 'Run Report'}
          </button>
        </div>
        {fetchError && <p className="text-sm text-red-500">{fetchError}</p>}
      </div>

      {/* Summary stat cards */}
      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <StatCard label="Tax Collected"       value={fmt$(summary.totalTax)}    accent="text-emerald-700" />
          <StatCard label="Taxable Sales"        value={fmt$(summary.totalTaxable)} />
          <StatCard label="Taxed Transactions"   value={summary.totalCount.toLocaleString()} />
          <StatCard label="Effective Rate"
            value={summary.totalTaxable > 0 ? fmtPct(summary.effectiveRate) : '—'}
            sub="tax ÷ taxable sales" />
        </div>
      )}

      {/* Remittance reminder */}
      {summary && summary.totalTax > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex gap-3 items-start">
          <span className="text-amber-500 text-lg leading-none shrink-0 mt-0.5">⚠</span>
          <p className="text-sm text-amber-800 leading-snug">
            <span className="font-semibold">Remittance reminder:</span> GoHost collects and tracks sales tax on your behalf.
            You are responsible for remitting these amounts to your state and local tax authority on your filing schedule.
            Consult your accountant or state revenue department for deadlines and filing instructions.
          </p>
        </div>
      )}

      {/* Monthly breakdown table */}
      {rows && rows.length > 0 && (
        <div className="bg-white border border-neutral-200 rounded-xl overflow-hidden">
          <div className="flex items-center justify-between px-5 py-3 border-b border-neutral-100">
            <p className="text-sm font-semibold text-neutral-700">Monthly Breakdown</p>
            <ExportBtn onClick={handleExport} label="Export CSV" />
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-neutral-100 bg-neutral-50">
                  {['Month', 'Taxable Sales', 'Tax Collected', 'Transactions', 'Effective Rate'].map(h => (
                    <th key={h} className="px-5 py-3 text-left text-xs font-semibold text-neutral-400 uppercase tracking-wide whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.month} className="border-b border-neutral-50 hover:bg-neutral-50 transition-colors">
                    <td className="px-5 py-3 font-medium text-neutral-800 whitespace-nowrap">{fmtMonth(r.month)}</td>
                    <td className="px-5 py-3 text-neutral-700 tabular-nums">{fmt$(r.taxable)}</td>
                    <td className="px-5 py-3 font-semibold text-neutral-900 tabular-nums">{fmt$(r.tax)}</td>
                    <td className="px-5 py-3 text-neutral-500 tabular-nums">{r.count.toLocaleString()}</td>
                    <td className="px-5 py-3 text-neutral-500">{r.taxable > 0 ? fmtPct(r.effectiveRate) : '—'}</td>
                  </tr>
                ))}
                {rows.length > 1 && (
                  <tr className="bg-neutral-50 border-t border-neutral-200 font-semibold">
                    <td className="px-5 py-3 text-neutral-700">Total</td>
                    <td className="px-5 py-3 tabular-nums">{fmt$(summary.totalTaxable)}</td>
                    <td className="px-5 py-3 tabular-nums text-emerald-700">{fmt$(summary.totalTax)}</td>
                    <td className="px-5 py-3 tabular-nums">{summary.totalCount.toLocaleString()}</td>
                    <td className="px-5 py-3">{fmtPct(summary.effectiveRate)}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {rows && rows.length === 0 && !loading && (
        <div className="bg-white border border-neutral-200 rounded-xl py-12 text-center">
          <p className="text-sm font-medium text-neutral-400">No tax collected in this period.</p>
          <p className="text-xs text-neutral-300 mt-1">Either no taxed transactions exist yet, or tax rate was 0% at time of payment.</p>
        </div>
      )}
    </div>
  );
};

// ── REPORT PANEL (header + period picker + content router) ─────────────────────
const ReportPanel = ({ report, periods, selectedPeriodId, onPeriodChange, aggregated, selectedPeriod, loading, restaurantId, smsLogs, smsLoading }) => {
  const needsPicker = !['labor_trend', 'custom_build', 'gc_overview', 'gc_export', 'sms_overview', 'sms_spend', 'sms_trend', 'exp_sms', 'shift_overview',
    'hr_roster', 'hr_terms', 'hr_newhires', 'hr_earnings', 'hr_tips', 'hr_fica', 'exp_w2_prep', 'sales_tax'].includes(report.id);

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold text-neutral-800">{report.title}</h3>
          <p className="text-sm text-neutral-400 mt-0.5">{report.desc}</p>
        </div>
        {needsPicker && (
          <PeriodPicker periods={periods} selectedId={selectedPeriodId} onChange={onPeriodChange} loading={loading.periods} />
        )}
      </div>

      {report.id === 'sales_tax'     && <SalesTaxReport     restaurantId={restaurantId} />}
      {report.id === 'manager_audit' && <ManagerAuditReport restaurantId={restaurantId} />}
      {report.id === 'labor_dept'   && <LaborDeptReport  aggregated={aggregated} period={selectedPeriod} loading={loading.ledger} />}
      {report.id === 'hours_ot'     && <HoursOTReport    aggregated={aggregated} loading={loading.ledger} />}
      {report.id === 'labor_role'   && <LaborRoleReport  aggregated={aggregated} loading={loading.ledger} />}
      {report.id === 'labor_trend'  && <LaborTrendReport allPeriods={periods} restaurantId={restaurantId} />}
      {report.id === 'rev_vs_labor' && <RevVsLaborReport aggregated={aggregated} period={selectedPeriod} loading={loading.ledger} />}
      {report.id === 'period_pl'    && <PeriodPLReport   aggregated={aggregated} period={selectedPeriod} loading={loading.ledger} restaurantId={restaurantId} />}
      {report.id === 'custom_build' && (
        <CustomReportBuilder aggregated={aggregated} periods={periods}
          selectedPeriodId={selectedPeriodId} onPeriodChange={onPeriodChange} loading={loading}
          smsLogs={smsLogs} smsLoading={smsLoading} restaurantId={restaurantId} />
      )}
      {report.id === 'sms_overview' && <SMSOverviewReport smsLogs={smsLogs} smsLoading={smsLoading} />}
      {report.id === 'sms_spend'    && <SMSOverviewReport smsLogs={smsLogs} smsLoading={smsLoading} />}
      {report.id === 'sms_trend'    && <SMSTrendReport    smsLogs={smsLogs} smsLoading={smsLoading} />}
      {report.id === 'exp_sms'      && <SMSExportPanel    smsLogs={smsLogs} smsLoading={smsLoading} />}
      {report.id === 'exp_payroll'  && <ExportPayrollPanel aggregated={aggregated} periods={periods} selectedPeriodId={selectedPeriodId} onPeriodChange={onPeriodChange} loading={loading} />}
      {report.id === 'exp_hours'    && <ExportHoursPanel   aggregated={aggregated} periods={periods} selectedPeriodId={selectedPeriodId} onPeriodChange={onPeriodChange} loading={loading} />}
      {report.id === 'exp_pl'       && <ExportPLPanel      aggregated={aggregated} periods={periods} selectedPeriodId={selectedPeriodId} onPeriodChange={onPeriodChange} loading={loading} />}
      {report.id === 'gc_overview'  && <GiftCardsReport    restaurantId={restaurantId} view="overview" />}
      {report.id === 'gc_export'    && <GiftCardsReport    restaurantId={restaurantId} view="export"   />}
      {report.id === 'shift_overview' && <ShiftOverviewReport restaurantId={restaurantId} />}
      {report.id === 'hr_roster'   && <HRRosterReport          restaurantId={restaurantId} />}
      {report.id === 'hr_terms'    && <HRTerminationLog         restaurantId={restaurantId} />}
      {report.id === 'hr_newhires' && <HRNewHireReport          restaurantId={restaurantId} />}
      {report.id === 'hr_earnings' && <HRAnnualEarningsReport   restaurantId={restaurantId} />}
      {report.id === 'hr_tips'     && <HRTipSummaryReport       restaurantId={restaurantId} />}
      {report.id === 'hr_fica'     && <HRFicaCreditReport       restaurantId={restaurantId} />}
      {report.id === 'exp_w2_prep' && <HRAnnualEarningsReport   restaurantId={restaurantId} w2Mode />}
    </div>
  );
};

// ── CATEGORY + REPORT DEFINITIONS ─────────────────────────────────────────────
const CATEGORIES = [
  { id: 'saved',     label: 'Saved'      },
  { id: 'financial', label: 'Financial'  },
  { id: 'labor',     label: 'Labor'          },
  { id: 'hr',        label: 'HR'             },
  { id: 'cash',      label: 'Cash & Shifts'  },
  { id: 'sms',       label: 'SMS'            },
  { id: 'giftcards', label: 'Gift Cards' },
  { id: 'custom',    label: 'Custom'     },
  { id: 'exports',   label: 'Exports'    },
];

const REPORT_DEFS = [
  { id: 'sales_tax',    category: 'financial', title: 'Sales Tax Summary',       desc: 'Tax collected by month — taxable sales, tax amount, effective rate, and CSV export for state filing.', tag: 'Date range' },
  { id: 'manager_audit', category: 'financial', title: 'Manager Audit Log',      desc: 'All voids, refunds, comps, and removed items with authorizer and amount.', tag: 'Live' },
  { id: 'rev_vs_labor', category: 'financial', title: 'Revenue vs Labor Cost',   desc: 'Labor cost as a % of revenue per pay period.',               tag: 'Pay-period' },
  { id: 'period_pl',    category: 'financial', title: 'Period P&L Summary',      desc: 'Revenue, labor cost, and estimated gross margin.',            tag: 'Pay-period' },
  { id: 'sms_spend',    category: 'financial', title: 'SMS Expenses',            desc: 'SMS and MMS usage cost breakdown — segments billed by type and month.', tag: 'Live' },
  { id: 'labor_dept',   category: 'labor',     title: 'Labor Cost by Department',desc: 'Cost and hours broken down by FOH, BOH, and Management.',     tag: 'Pay-period' },
  { id: 'hours_ot',     category: 'labor',     title: 'Hours & Overtime',        desc: 'Regular and overtime hours by employee.',                     tag: 'Pay-period' },
  { id: 'labor_role',   category: 'labor',     title: 'Labor Cost by Role',      desc: 'Cost and hours breakdown across all positions.',              tag: 'Pay-period' },
  { id: 'labor_trend',  category: 'labor',     title: 'Labor Cost Trend',        desc: 'Labor cost and % of revenue across the last 12 periods.',     tag: 'Multi-period' },
  { id: 'shift_overview', category: 'cash',   title: 'Shift & Cash Drawer Report', desc: 'Open/close history, over/short alerts, paid out/in drill-down, and time correction audit trail.', tag: 'Live' },
  { id: 'hr_roster',   category: 'hr', title: 'Employee Roster',         desc: 'Full staff directory with roles, pay rates, and hire dates. Includes active and terminated employees.', tag: 'Live' },
  { id: 'hr_terms',    category: 'hr', title: 'Termination Log',          desc: 'All terminated employees with termination date, reason, final pay status, and rehire eligibility.',      tag: 'Live' },
  { id: 'hr_newhires', category: 'hr', title: 'New Hire Report',          desc: 'Employees added within a calendar year. Most states require new hire reporting within 20 days.',         tag: 'By Year' },
  { id: 'hr_earnings', category: 'hr', title: 'Annual Earnings Summary',  desc: 'Total wages, tips, overtime, and gross pay per employee for the year. Accountant-ready for W-2 prep.',  tag: 'By Year' },
  { id: 'hr_tips',     category: 'hr', title: 'Annual Tip Summary',       desc: 'Total tips reported per tipped employee for the year. Required data for IRS Form 8027.',                tag: 'By Year' },
  { id: 'hr_fica',     category: 'hr', title: 'FICA Tip Credit (§45B)',   desc: 'Estimated employer tax credit on tips above federal minimum wage. Prep data for Form 8846.',            tag: 'By Year' },
  { id: 'sms_overview', category: 'sms',       title: 'SMS Overview',            desc: 'Messages sent, delivery rate, segments billed, and breakdown by message type.', tag: 'Live' },
  { id: 'sms_trend',    category: 'sms',       title: 'SMS Volume Trend',        desc: 'Monthly message and segment volume over the last 12 months.', tag: 'Multi-month' },
  { id: 'gc_overview',  category: 'giftcards', title: 'Gift Card Activity',       desc: 'Sales, outstanding liability, redeemed value, and full card log.', tag: 'Live' },
  { id: 'gc_export',    category: 'giftcards', title: 'Gift Card Export',         desc: 'Download full gift card transaction history as a CSV.',            tag: 'CSV' },
  { id: 'custom_build', category: 'custom',    title: 'Custom Report Builder',   desc: 'Choose any metric and grouping to build your own view.',      tag: 'Interactive' },
  { id: 'exp_payroll',  category: 'exports',   title: 'Payroll Summary CSV',     desc: 'Labor cost summary for internal review. For payroll submission, use the Payroll tab.',  tag: 'CSV' },
  { id: 'exp_hours',    category: 'exports',   title: 'Hours & OT CSV',          desc: 'Hours and overtime data for HR or compliance review.',        tag: 'CSV' },
  { id: 'exp_pl',       category: 'exports',   title: 'P&L Summary CSV',         desc: 'Revenue and labor cost summary, accountant-ready.',           tag: 'CSV' },
  { id: 'exp_sms',      category: 'exports',   title: 'SMS Log CSV',             desc: 'Full message log with type, status, segments, and Twilio SID.', tag: 'CSV' },
  { id: 'exp_w2_prep', category: 'exports',   title: 'W-2 Prep Export',         desc: 'Annual wages and tips per employee formatted for handoff to your payroll processor or accountant.', tag: 'CSV' },
];

// ── MAIN COMPONENT ─────────────────────────────────────────────────────────────
const ReportsView = ({ user, restaurantId }) => {
  const [category,         setCategory]         = useState('financial');
  const [activeReportId,   setActiveReportId]   = useState(null);
  const [search,           setSearch]           = useState('');
  const [periods,          setPeriods]          = useState([]);
  const [selectedPeriodId, setSelectedPeriodId] = useState(null);
  const [ledger,           setLedger]           = useState([]);
  const [empMap,           setEmpMap]           = useState({});
  const [loading,          setLoading]          = useState({ periods: true, ledger: false });
  const [smsLogs,          setSmsLogs]          = useState([]);
  const [smsLoading,       setSmsLoading]       = useState(true);
  const [savedReports,     setSavedReports]     = useState([]);

  // Load employees
  useEffect(() => {
    return onSnapshot(
      collection(db, 'restaurants_data', restaurantId, 'employees'),
      snap => {
        const m = {};
        snap.docs.forEach(d => { m[d.id] = { id: d.id, ...d.data() }; });
        setEmpMap(m);
      }
    );
  }, [restaurantId]);

  // Load pay periods
  useEffect(() => {
    return onSnapshot(
      query(collection(db, 'restaurants_data', restaurantId, 'payPeriods'), orderBy('startDate', 'desc'), limit(24)),
      snap => {
        const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        setPeriods(list);
        if (list.length) setSelectedPeriodId(prev => prev || list[0].id);
        setLoading(l => ({ ...l, periods: false }));
      },
      () => setLoading(l => ({ ...l, periods: false })),
    );
  }, [restaurantId]);

  // Load ledger for selected period
  useEffect(() => {
    if (!selectedPeriodId) return;
    setLoading(l => ({ ...l, ledger: true }));
    return onSnapshot(
      query(collection(db, 'restaurants_data', restaurantId, 'payroll_ledger'), where('payPeriodId', '==', selectedPeriodId)),
      snap => {
        setLedger(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        setLoading(l => ({ ...l, ledger: false }));
      },
      () => setLoading(l => ({ ...l, ledger: false })),
    );
  }, [restaurantId, selectedPeriodId]);

  // Load SMS logs
  useEffect(() => {
    setSmsLoading(true);
    return onSnapshot(
      query(
        collection(db, 'restaurants_data', restaurantId, 'sms_logs'),
        orderBy('createdAt', 'desc'),
        limit(2000),
      ),
      snap => {
        setSmsLogs(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        setSmsLoading(false);
      },
      () => setSmsLoading(false),
    );
  }, [restaurantId]);

  // Load saved reports
  useEffect(() => {
    return onSnapshot(
      query(collection(db, 'restaurants_data', restaurantId, 'saved_reports'), orderBy('createdAt', 'desc')),
      snap => setSavedReports(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
    );
  }, [restaurantId]);

  const aggregated     = useMemo(() => aggregateLedger(ledger, empMap), [ledger, empMap]);
  const selectedPeriod = periods.find(p => p.id === selectedPeriodId);
  const activeReport   = REPORT_DEFS.find(r => r.id === activeReportId);

  const visibleReports = useMemo(() => {
    if (category === 'saved') return [];
    const base = REPORT_DEFS.filter(r => r.category === category);
    if (!search.trim()) return base;
    const q = search.toLowerCase();
    return base.filter(r => r.title.toLowerCase().includes(q) || r.desc.toLowerCase().includes(q));
  }, [category, search]);

  const handleBack = () => { setActiveReportId(null); setSearch(''); };

  return (
    <div className="space-y-6">
      {activeReportId && (
        <button onClick={handleBack} className="flex items-center gap-1.5 text-sm text-neutral-500 hover:text-neutral-800 transition-colors">
          ← Back to Reports
        </button>
      )}

      {!activeReportId && (
        <>
          {/* Category tabs + search */}
          <div className="flex flex-wrap items-center gap-3 justify-between border-b border-neutral-200 pb-3">
            <div className="flex gap-2 flex-wrap">
              {CATEGORIES.map(cat => (
                <button key={cat.id} onClick={() => { setCategory(cat.id); setSearch(''); }}
                  className={`text-sm font-medium px-4 py-1.5 rounded-lg transition-colors ${
                    category === cat.id ? 'bg-neutral-900 text-white' : 'text-neutral-500 hover:text-neutral-800'
                  }`}>
                  {cat.label}
                </button>
              ))}
            </div>
            <input
              type="text" value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search reports…"
              className="text-sm border border-neutral-300 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-neutral-300 w-44"
            />
          </div>

          {/* Report cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Saved reports — shown when category === 'saved' */}
            {category === 'saved' && (
              savedReports.length === 0 ? (
                <div className="col-span-2 py-16 text-center">
                  <p className="text-sm font-medium text-neutral-400">No saved reports yet.</p>
                  <p className="text-sm text-neutral-300 mt-1">Build a custom report and click "Save Report" to pin it here.</p>
                </div>
              ) : (
                <div className="col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {savedReports.map(r => (
                    <div key={r.id} className="relative text-left p-5 bg-white border border-neutral-200 rounded-xl hover:border-neutral-400 hover:shadow-sm transition-all group">
                      <button className="w-full text-left" onClick={() => setActiveReportId(r.reportId)}>
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <p className="text-sm font-semibold text-neutral-800 leading-tight mb-1">{r.name}</p>
                            <p className="text-sm text-neutral-400 leading-snug capitalize">
                              {r.config?.dataSource === 'sms' ? 'SMS — ' : 'Payroll — '}
                              {r.config?.dataSource === 'sms'
                                ? SMS_CUSTOM_METRICS.find(m => m.id === r.config?.smsMetric)?.label
                                : CUSTOM_METRICS.find(m => m.id === r.config?.metric)?.label}
                              {' by '}
                              {r.config?.dataSource === 'sms'
                                ? (r.config?.smsGroupBy === 'type' ? 'Type' : r.config?.smsGroupBy === 'status' ? 'Status' : 'Month')
                                : CUSTOM_GROUPS.find(g => g.id === r.config?.groupBy)?.label}
                            </p>
                          </div>
                          <span className="text-neutral-300 group-hover:text-neutral-600 text-sm shrink-0 mt-0.5 transition-colors">→</span>
                        </div>
                        <span className="mt-3 inline-block text-xs font-medium text-neutral-500 bg-neutral-100 rounded px-2 py-0.5">Saved</span>
                      </button>
                      <button
                        onClick={async () => {
                          try { await deleteDoc(doc(db, 'restaurants_data', restaurantId, 'saved_reports', r.id)); } catch (_) {}
                        }}
                        className="absolute top-3 right-3 text-neutral-300 hover:text-red-500 transition-colors text-sm"
                        title="Remove"
                      >✕</button>
                    </div>
                  ))}
                </div>
              )
            )}
            {category !== 'saved' && (visibleReports.length > 0 ? visibleReports.map(report => (
              <button key={report.id} onClick={() => setActiveReportId(report.id)}
                className="text-left p-5 bg-white border border-neutral-200 rounded-xl hover:border-neutral-400 hover:shadow-sm transition-all group">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-neutral-800 leading-tight mb-1">{report.title}</p>
                    <p className="text-sm text-neutral-400 leading-snug">{report.desc}</p>
                  </div>
                  <span className="text-neutral-300 group-hover:text-neutral-600 text-sm shrink-0 mt-0.5 transition-colors">→</span>
                </div>
                <span className="mt-3 inline-block text-xs font-medium text-neutral-500 bg-neutral-100 rounded px-2 py-0.5">
                  {report.tag}
                </span>
              </button>
            )) : (
              <p className="text-sm text-neutral-400 col-span-2 py-10 text-center">No reports match "{search}".</p>
            ))}
          </div>
        </>
      )}

      {activeReportId && activeReport && (
        <ReportPanel
          report={activeReport}
          periods={periods}
          selectedPeriodId={selectedPeriodId}
          onPeriodChange={setSelectedPeriodId}
          aggregated={aggregated}
          selectedPeriod={selectedPeriod}
          loading={loading}
          restaurantId={restaurantId}
          smsLogs={smsLogs}
          smsLoading={smsLoading}
        />
      )}
    </div>
  );
};

export default ReportsView;
